import React, { useState, useEffect, createContext, useContext, useCallback } from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate, useNavigate, Link, useLocation } from "react-router-dom";
import axios from "axios";
import { Calendar } from "@/components/ui/calendar";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle } from "@/components/ui/alert-dialog";
import { QRCodeCanvas } from "qrcode.react";
import { Toaster } from "@/components/ui/sonner";
import { toast } from "sonner";
import { format, addDays, isMonday, isSunday, startOfMonth, endOfMonth, eachDayOfInterval, isSameDay, parseISO } from "date-fns";
import { it } from "date-fns/locale";
import { 
  Scissors, 
  Calendar as CalendarIcon, 
  LogOut, 
  Menu, 
  X, 
  Clock, 
  Phone, 
  Mail,
  Image,
  Plus,
  Trash2,
  CheckCircle,
  XCircle,
  Users,
  BarChart3,
  ChevronLeft,
  ChevronRight,
  Package,
  Settings as SettingsIcon,
  Award,
  Edit3
} from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Configure axios
axios.defaults.withCredentials = true;

// Settings context: fetches site settings and exposes them + applies CSS variables
const SettingsContext = createContext({ settings: {}, refresh: () => {} });
const useSettings = () => useContext(SettingsContext);

const SettingsProvider = ({ children }) => {
  const [settings, setSettings] = useState({});

  const refresh = useCallback(async () => {
    try {
      const res = await axios.get(`${API}/settings`);
      setSettings(res.data || {});
    } catch (e) {
      // silent fail — keep defaults
    }
  }, []);

  useEffect(() => { refresh(); }, [refresh]);

  // Apply dynamic CSS variables when settings change
  useEffect(() => {
    if (!settings) return;
    const root = document.documentElement;
    if (settings.color_primary) root.style.setProperty('--gold', settings.color_primary);
    if (settings.color_bg) root.style.setProperty('--bg-dark', settings.color_bg);
    if (settings.color_text) root.style.setProperty('--text-light', settings.color_text);
    if (settings.salon_name) document.title = `${settings.salon_name} - ${settings.salon_tagline || ''}`.trim();
  }, [settings]);

  return (
    <SettingsContext.Provider value={{ settings, refresh, setSettings }}>
      {children}
    </SettingsContext.Provider>
  );
};

// useReveal: IntersectionObserver hook that adds .is-visible to .reveal elements when they enter viewport
const useReveal = (deps = []) => {
  useEffect(() => {
    const els = document.querySelectorAll('.reveal:not(.is-visible)');
    if (!('IntersectionObserver' in window)) {
      els.forEach(el => el.classList.add('is-visible'));
      return;
    }
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.classList.add('is-visible');
          observer.unobserve(entry.target);
        }
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -60px 0px' });
    els.forEach(el => observer.observe(el));
    return () => observer.disconnect();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, deps);
};

// Auth Context
const AuthContext = createContext(null);

const useAuth = () => useContext(AuthContext);

const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAuth = useCallback(async () => {
    // CRITICAL: If returning from OAuth callback, skip the /me check.
    // AuthCallback will exchange the session_id and establish the session first.
    if (window.location.hash?.includes('session_id=')) {
      setLoading(false);
      return;
    }

    try {
      const response = await axios.get(`${API}/auth/me`);
      setUser(response.data);
    } catch (error) {
      setUser(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAuth();
  }, [checkAuth]);

  const login = (userData) => {
    setUser(userData);
  };

  const logout = async () => {
    try {
      await axios.post(`${API}/auth/logout`);
    } catch (e) {
      console.error("Logout error:", e);
    }
    setUser(null);
  };

  return (
    <AuthContext.Provider value={{ user, login, logout, loading, checkAuth }}>
      {children}
    </AuthContext.Provider>
  );
};

// Admin Auth Context
const AdminAuthContext = createContext(null);

const useAdminAuth = () => useContext(AdminAuthContext);

const AdminAuthProvider = ({ children }) => {
  const [admin, setAdmin] = useState(null);
  const [loading, setLoading] = useState(true);

  const checkAdminAuth = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/auth/admin/me`);
      setAdmin(response.data);
    } catch (error) {
      setAdmin(null);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    checkAdminAuth();
  }, [checkAdminAuth]);

  const loginAdmin = (adminData) => {
    setAdmin(adminData);
  };

  const logoutAdmin = async () => {
    try {
      await axios.post(`${API}/auth/admin/logout`);
    } catch (e) {
      console.error("Logout error:", e);
    }
    setAdmin(null);
  };

  return (
    <AdminAuthContext.Provider value={{ admin, loginAdmin, logoutAdmin, loading, checkAdminAuth }}>
      {children}
    </AdminAuthContext.Provider>
  );
};

// Auth Callback Component
const AuthCallback = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const { logoutAdmin } = useAdminAuth();
  const hasProcessed = React.useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const processAuth = async () => {
      const hash = window.location.hash;
      const sessionId = new URLSearchParams(hash.substring(1)).get('session_id');

      if (!sessionId) {
        navigate('/');
        return;
      }

      try {
        await logoutAdmin();
        const response = await axios.post(`${API}/auth/session`, { session_id: sessionId });
        login(response.data.user);
        toast.success(`Benvenuto, ${response.data.user.name}!`);
        // Clear hash and redirect
        window.history.replaceState(null, '', '/prenota');
        navigate('/prenota', { replace: true, state: { user: response.data.user } });
      } catch (error) {
        console.error("Auth error:", error);
        toast.error("Errore durante l'accesso");
        navigate('/');
      }
    };

    processAuth();
  }, [navigate, login, logoutAdmin]);

  return (
    <div className="min-h-screen flex items-center justify-center">
      <div className="text-center">
        <Scissors className="w-10 h-10 mx-auto mb-4 animate-pulse" />
        <p className="text-neutral-500">Accesso in corso...</p>
      </div>
    </div>
  );
};

// Navigation Component
const Navigation = () => {
  const { user, logout } = useAuth();
  const { admin, logoutAdmin } = useAdminAuth();
  const [menuOpen, setMenuOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
    };
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const handleLogout = async () => {
    if (admin) {
      await logoutAdmin();
      toast.success("Logout admin effettuato");
    } else {
      await logout();
      toast.success("Logout effettuato");
    }
    navigate("/");
  };

  const currentUser = admin || user;

  return (
    <nav className={`fixed top-0 w-full z-50 transition-all duration-500 ${scrolled ? 'glass-effect shadow-sm' : 'bg-transparent'}`} data-testid="main-navigation">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="flex items-center justify-between h-20">
          <Link to="/" className="flex items-center gap-3 group" data-testid="logo-link">
            <div className="w-10 h-10 border border-[#C9A962] rounded-full flex items-center justify-center group-hover:bg-[#C9A962] transition-all duration-300">
              <Scissors className={`w-4 h-4 ${scrolled ? 'text-[#C9A962]' : 'text-white'} group-hover:text-white transition-colors`} />
            </div>
            <div>
              <span className={`font-serif text-lg tracking-wide ${scrolled ? 'text-black' : 'text-white'}`}>SEVENTEEN</span>
              <span className={`block text-[10px] tracking-[0.3em] ${scrolled ? 'text-[#C9A962]' : 'text-[#C9A962]'}`}>BARBER</span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden md:flex items-center gap-10">
            <Link to="/" className={`nav-link ${scrolled ? 'text-black' : 'text-white'}`} data-testid="nav-home">Home</Link>
            <Link to="/prenota" className={`nav-link ${scrolled ? 'text-black' : 'text-white'}`} data-testid="nav-book">Prenota</Link>
            <Link to="/gallery" className={`nav-link ${scrolled ? 'text-black' : 'text-white'}`} data-testid="nav-gallery">Gallery</Link>
            {admin ? (
              <>
                <Link to="/admin" className={`nav-link ${scrolled ? 'text-black' : 'text-white'}`} data-testid="nav-admin">Admin</Link>
                <button onClick={handleLogout} className={`nav-link flex items-center gap-2 ${scrolled ? 'text-black' : 'text-white'}`} data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : user ? (
              <>
                <Link to="/dashboard" className={`nav-link ${scrolled ? 'text-black' : 'text-white'}`} data-testid="nav-dashboard">Prenotazioni</Link>
                <button onClick={handleLogout} className={`nav-link flex items-center gap-2 ${scrolled ? 'text-black' : 'text-white'}`} data-testid="logout-btn">
                  <LogOut className="w-4 h-4" />
                </button>
              </>
            ) : (
              <Link to="/login" className="btn-gold px-6 py-2 text-xs uppercase tracking-[0.15em] font-medium" data-testid="nav-login">Accedi</Link>
            )}
          </div>

          {/* Mobile Menu Toggle */}
          <button 
            className={`md:hidden p-2 ${scrolled ? 'text-black' : 'text-white'}`}
            onClick={() => setMenuOpen(!menuOpen)}
            data-testid="mobile-menu-toggle"
          >
            {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
        </div>

        {/* Mobile Menu */}
        {menuOpen && (
          <div className="md:hidden py-6 border-t border-neutral-200 bg-white" data-testid="mobile-menu">
            <div className="flex flex-col gap-4">
              <Link to="/" className="nav-link text-black py-2" onClick={() => setMenuOpen(false)}>Home</Link>
              <Link to="/prenota" className="nav-link text-black py-2" onClick={() => setMenuOpen(false)}>Prenota</Link>
              <Link to="/gallery" className="nav-link text-black py-2" onClick={() => setMenuOpen(false)}>Gallery</Link>
              {admin ? (
                <>
                  <Link to="/admin" className="nav-link text-black py-2" onClick={() => setMenuOpen(false)}>Admin</Link>
                  <button onClick={handleLogout} className="nav-link text-black text-left py-2">Logout</button>
                </>
              ) : user ? (
                <>
                  <Link to="/dashboard" className="nav-link text-black py-2" onClick={() => setMenuOpen(false)}>Le Mie Prenotazioni</Link>
                  <button onClick={handleLogout} className="nav-link text-black text-left py-2">Logout</button>
                </>
              ) : (
                <Link to="/login" className="btn-gold text-center py-3 mt-2" onClick={() => setMenuOpen(false)}>Accedi</Link>
              )}
            </div>
          </div>
        )}
      </div>
    </nav>
  );
};

// Google Login Button
const GoogleLoginButton = () => {
  const handleGoogleLogin = () => {
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  return (
    <button
      onClick={handleGoogleLogin}
      className="w-full flex items-center justify-center gap-4 bg-white border border-neutral-200 py-5 px-6 hover:border-[#C9A962] hover:shadow-lg transition-all duration-300 group"
      data-testid="google-login-btn"
    >
      <svg className="w-5 h-5" viewBox="0 0 24 24">
        <path fill="#4285F4" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
        <path fill="#34A853" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
        <path fill="#FBBC05" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
        <path fill="#EA4335" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
      </svg>
      <span className="text-sm font-medium uppercase tracking-[0.15em] group-hover:text-[#C9A962] transition-colors">Accedi con Google</span>
    </button>
  );
};

// Not Found Page
const NotFoundPage = () => {
  const navigate = useNavigate();
  return (
    <div className="min-h-screen bg-[#0a0a0a] flex flex-col items-center justify-center px-6 text-center">
      <p className="text-[#C9A962] text-xs uppercase tracking-[0.4em] mb-4">Errore 404</p>
      <h1 className="font-serif text-5xl md:text-7xl text-white mb-4">Pagina non trovata</h1>
      <p className="text-neutral-400 mb-8 max-w-md">La pagina che cerchi non esiste o è stata spostata.</p>
      <button
        onClick={() => navigate('/')}
        className="px-8 py-3 bg-[#C9A962] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all"
        data-testid="notfound-home-btn"
      >
        Torna alla Home
      </button>
    </div>
  );
};

// Home Page
const HomePage = () => {
  const [services, setServices] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [barbers, setBarbers] = useState([]);
  const [products, setProducts] = useState([]);
  const [mousePosition, setMousePosition] = useState({ x: 0, y: 0 });
  const [requestProduct, setRequestProduct] = useState(null);
  const [reqForm, setReqForm] = useState({ customer_name: "", customer_email: "", customer_phone: "", notes: "" });
  const navigate = useNavigate();
  const { settings } = useSettings();

  useReveal([services.length, gallery.length, barbers.length, products.length]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [servicesRes, galleryRes, barbersRes, productsRes] = await Promise.all([
          axios.get(`${API}/services`),
          axios.get(`${API}/gallery`),
          axios.get(`${API}/barbers`),
          axios.get(`${API}/products`)
        ]);
        // Deduplicate services by name (multiple barbers may have same service name)
        const seen = new Set();
        const uniqueServices = servicesRes.data.filter(s => {
          const k = s.name.toLowerCase().trim();
          if (seen.has(k)) return false;
          seen.add(k);
          return true;
        });
        setServices(uniqueServices);
        setGallery(galleryRes.data.slice(0, 6));
        setBarbers(barbersRes.data);
        setProducts(productsRes.data);
      } catch (e) {
        console.error("Error fetching data:", e);
      }
    };
    fetchData();

    const handleMouseMove = (e) => {
      setMousePosition({ x: e.clientX, y: e.clientY });
    };
    window.addEventListener('mousemove', handleMouseMove);
    return () => window.removeEventListener('mousemove', handleMouseMove);
  }, []);

  return (
    <div className="min-h-screen bg-[#0a0a0a]" data-testid="home-page">
      {/* Hero Section - Full Screen Modern */}
      <section className="relative h-screen flex items-center justify-center overflow-hidden" data-testid="hero-section">
        {/* Animated Background */}
        <div className="absolute inset-0">
          <div 
            className="absolute inset-0 bg-cover bg-center transition-transform duration-1000 ease-out"
            style={{ 
              backgroundImage: `url(https://images.unsplash.com/photo-1585747860715-2ba37e788b70?crop=entropy&cs=srgb&fm=jpg&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&ixlib=rb-4.0.3&q=85)`,
              filter: 'grayscale(100%) brightness(0.4)',
              transform: `scale(1.1) translate(${mousePosition.x * 0.01}px, ${mousePosition.y * 0.01}px)`
            }}
          />
        </div>
        
        {/* Gradient Overlay */}
        <div className="absolute inset-0 bg-gradient-to-b from-black/50 via-transparent to-[#0a0a0a]" />
        
        {/* Animated Lines */}
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-0 left-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#C9A962]/20 to-transparent" />
          <div className="absolute top-0 right-1/4 w-px h-full bg-gradient-to-b from-transparent via-[#C9A962]/20 to-transparent" />
        </div>

        {/* Corner Frames */}
        <div className="absolute top-8 left-8 w-24 h-24">
          <div className="absolute top-0 left-0 w-full h-px bg-gradient-to-r from-[#C9A962] to-transparent" />
          <div className="absolute top-0 left-0 h-full w-px bg-gradient-to-b from-[#C9A962] to-transparent" />
        </div>
        <div className="absolute top-8 right-8 w-24 h-24">
          <div className="absolute top-0 right-0 w-full h-px bg-gradient-to-l from-[#C9A962] to-transparent" />
          <div className="absolute top-0 right-0 h-full w-px bg-gradient-to-b from-[#C9A962] to-transparent" />
        </div>
        <div className="absolute bottom-8 left-8 w-24 h-24">
          <div className="absolute bottom-0 left-0 w-full h-px bg-gradient-to-r from-[#C9A962] to-transparent" />
          <div className="absolute bottom-0 left-0 h-full w-px bg-gradient-to-t from-[#C9A962] to-transparent" />
        </div>
        <div className="absolute bottom-8 right-8 w-24 h-24">
          <div className="absolute bottom-0 right-0 w-full h-px bg-gradient-to-l from-[#C9A962] to-transparent" />
          <div className="absolute bottom-0 right-0 h-full w-px bg-gradient-to-t from-[#C9A962] to-transparent" />
        </div>
        
        <div className="relative z-10 text-center text-white px-6 max-w-5xl">
          <div className="inline-block mb-8 animate-fade-in-up opacity-0" style={{ animationDelay: '0.1s', animationFillMode: 'forwards' }}>
            <span className="px-6 py-2 border border-[#C9A962]/50 text-[10px] uppercase tracking-[0.4em] text-[#C9A962]">
              Est. 2017 · Torre del Greco
            </span>
          </div>
          
          <h1 className="font-serif text-6xl md:text-8xl lg:text-9xl tracking-tight leading-[0.85] mb-6 animate-fade-in-up opacity-0" style={{ animationDelay: '0.2s', animationFillMode: 'forwards' }}>
            <span className="block text-white">SEVENTEEN</span>
            <span className="block text-[#C9A962] italic font-light">Barber</span>
          </h1>
          
          <div className="flex items-center justify-center gap-4 mb-10 animate-fade-in-up opacity-0" style={{ animationDelay: '0.3s', animationFillMode: 'forwards' }}>
            <div className="h-px w-12 bg-gradient-to-r from-transparent to-[#C9A962]" />
            <p className="text-sm tracking-[0.3em] uppercase text-neutral-400">Premium Grooming Experience</p>
            <div className="h-px w-12 bg-gradient-to-l from-transparent to-[#C9A962]" />
          </div>
          
          <button 
            onClick={() => navigate('/prenota')}
            className="group relative px-12 py-5 overflow-hidden animate-fade-in-up opacity-0"
            style={{ animationDelay: '0.4s', animationFillMode: 'forwards' }}
            data-testid="hero-book-btn"
          >
            <span className="absolute inset-0 bg-[#C9A962]" />
            <span className="absolute inset-0 bg-white transform -translate-x-full group-hover:translate-x-0 transition-transform duration-500" />
            <span className="relative z-10 text-sm font-semibold uppercase tracking-[0.25em] text-black group-hover:text-black transition-colors">
              Prenota il tuo Stile
            </span>
          </button>
        </div>
        
        {/* Scroll Indicator */}
        <div className="absolute bottom-12 left-1/2 -translate-x-1/2 flex flex-col items-center gap-3 animate-fade-in-up opacity-0" style={{ animationDelay: '0.6s', animationFillMode: 'forwards' }}>
          <span className="text-[10px] uppercase tracking-[0.3em] text-neutral-500">Scopri</span>
          <div className="w-px h-12 bg-gradient-to-b from-[#C9A962] to-transparent animate-pulse" />
        </div>
      </section>

      {/* Services Section - Modern Grid */}
      <section className="py-32 px-6 md:px-12 lg:px-24 bg-[#0a0a0a]" data-testid="services-section">
        <div className="max-w-7xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-20 items-center mb-20">
            <div className="reveal">
              <span className="inline-block px-4 py-2 bg-[#C9A962]/10 text-[#C9A962] text-[10px] uppercase tracking-[0.3em] mb-6">
                I Nostri Servizi
              </span>
              <h2 className="font-serif text-5xl md:text-6xl text-white leading-tight mb-6">
                Precisione<br />
                <span className="text-[#C9A962] italic">Artigianale</span>
              </h2>
              <p className="text-neutral-500 leading-relaxed max-w-md">
                Ogni taglio è un'opera d'arte. Combiniamo tecniche tradizionali italiane con uno stile contemporaneo per un risultato unico.
              </p>
            </div>
            
            <div className="grid grid-cols-1 gap-4">
              {services.map((service, index) => (
                <div 
                  key={service.id} 
                  className="reveal group relative p-8 bg-neutral-900/50 border border-neutral-800 hover:border-[#C9A962]/50 transition-all duration-500 cursor-pointer overflow-hidden hover:translate-y-[-4px]"
                  style={{ transitionDelay: `${index * 80}ms` }}
                  data-testid={`service-card-${index}`}
                >
                  <div className="absolute inset-0 bg-gradient-to-r from-[#C9A962]/5 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-500" />
                  
                  <div className="relative z-10 flex items-center justify-between">
                    <div className="flex items-center gap-6">
                      <div className="w-14 h-14 rounded-full border border-[#C9A962]/30 flex items-center justify-center group-hover:border-[#C9A962] group-hover:bg-[#C9A962]/10 transition-all duration-500">
                        <Scissors className="w-5 h-5 text-[#C9A962] group-hover:rotate-[-45deg] transition-transform duration-500" />
                      </div>
                      <div>
                        <h3 className="font-serif text-2xl text-white mb-1">{service.name}</h3>
                        <p className="text-neutral-500 text-sm flex items-center gap-2">
                          <Clock className="w-3 h-3" />
                          {service.duration} minuti
                        </p>
                      </div>
                    </div>
                    <div className="text-right">
                      <p className="text-3xl font-serif text-[#C9A962]">€{service.price}</p>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Gallery Section - Masonry Style */}
      {gallery.length > 0 && (
        <section className="py-32 bg-white" data-testid="gallery-preview-section">
          <div className="max-w-7xl mx-auto px-6 md:px-12 lg:px-24">
          <div className="text-center mb-16 reveal">
            <span className="inline-block px-4 py-2 bg-black text-white text-[10px] uppercase tracking-[0.3em] mb-6">
                Portfolio
              </span>
              <h2 className="font-serif text-5xl md:text-6xl text-black">
                I Nostri <span className="text-[#C9A962] italic">Lavori</span>
              </h2>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
              {gallery.map((item, index) => (
                <div 
                  key={item.id} 
                  className={`reveal relative overflow-hidden group ${index === 0 ? 'md:col-span-2 md:row-span-2' : ''}`}
                  style={{ transitionDelay: `${index * 70}ms` }}
                  data-testid={`gallery-preview-${index}`}
                >
                  <div className={`${index === 0 ? 'aspect-square' : 'aspect-square'}`}>
                    <img 
                      src={item.media_url} 
                      alt={item.title}
                      loading="lazy"
                      className="w-full h-full object-cover filter grayscale hover:grayscale-0 transition-all duration-700 group-hover:scale-110"
                    />
                  </div>
                  <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/0 to-black/0 opacity-0 group-hover:opacity-100 transition-all duration-500">
                    <div className="absolute bottom-0 left-0 right-0 p-6 transform translate-y-4 group-hover:translate-y-0 transition-transform duration-500">
                      <div className="w-8 h-px bg-[#C9A962] mb-3" />
                      <h3 className="font-serif text-xl text-white">{item.title}</h3>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            
            <div className="text-center mt-12">
              <Link 
                to="/gallery" 
                className="inline-flex items-center gap-3 text-sm uppercase tracking-[0.2em] text-black hover:text-[#C9A962] transition-colors group"
                data-testid="view-all-gallery"
              >
                <span>Esplora la Gallery</span>
                <span className="group-hover:translate-x-2 transition-transform">→</span>
              </Link>
            </div>
          </div>
        </section>
      )}

      {/* Info Section - Split Design */}
      <section className="relative" data-testid="info-section">
        <div className="grid grid-cols-1 lg:grid-cols-2">
          {/* Left - Map/Image */}
          <div className="reveal relative h-[500px] lg:h-auto bg-neutral-900">
            <div 
              className="absolute inset-0 bg-cover bg-center opacity-50"
              style={{ 
                backgroundImage: `url(https://images.unsplash.com/photo-1503951914875-452162b0f3f1?crop=entropy&cs=srgb&fm=jpg&q=85)`,
                filter: 'grayscale(100%)'
              }}
            />
            <div className="absolute inset-0 bg-gradient-to-r from-[#0a0a0a] to-transparent" />
            <div className="relative z-10 h-full flex items-center justify-center p-12">
              <div className="text-center">
                <div className="w-20 h-20 mx-auto mb-6 border border-[#C9A962] rounded-full flex items-center justify-center">
                  <Scissors className="w-8 h-8 text-[#C9A962]" />
                </div>
                <p className="text-[#C9A962] text-sm uppercase tracking-[0.3em] mb-2">Dal 2017</p>
                <p className="text-white font-serif text-3xl">Torre del Greco</p>
              </div>
            </div>
          </div>
          
          {/* Right - Info */}
          <div className="reveal bg-[#0a0a0a] p-12 lg:p-20">
            <div className="max-w-lg">
              <span className="inline-block px-4 py-2 bg-[#C9A962]/10 text-[#C9A962] text-[10px] uppercase tracking-[0.3em] mb-8">
                Informazioni
              </span>
              
              <div className="space-y-10">
                <div>
                  <h3 className="font-serif text-2xl text-white mb-4">Orari</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between text-neutral-400 pb-3 border-b border-neutral-800">
                      <span>Martedì - Sabato</span>
                      <span className="text-white font-medium">10:00 - 19:00</span>
                    </div>
                    <div className="flex justify-between text-neutral-600 pb-3 border-b border-neutral-800">
                      <span>Domenica - Lunedì</span>
                      <span>Chiuso</span>
                    </div>
                  </div>
                </div>
                
                <div>
                  <h3 className="font-serif text-2xl text-white mb-4">Contatti</h3>
                  <a href="mailto:ernesto.petrucci2004@gmail.com" className="flex items-center gap-3 text-neutral-400 hover:text-[#C9A962] transition-colors mb-2">
                    <Mail className="w-4 h-4 text-[#C9A962]" />
                    ernesto.petrucci2004@gmail.com
                  </a>
                  <a href="mailto:giuseppe.petrucci2009@gmail.com" className="flex items-center gap-3 text-neutral-400 hover:text-[#C9A962] transition-colors">
                    <Mail className="w-4 h-4 text-[#C9A962]" />
                    giuseppe.petrucci2009@gmail.com
                  </a>
                </div>
                
                <div>
                  <h3 className="font-serif text-2xl text-white mb-4">Indirizzo</h3>
                  <p className="text-neutral-400 mb-4">
                    Via Cesare Battisti, 36<br />
                    80059 Torre del Greco (NA)
                  </p>
                  <a 
                    href="https://www.google.com/maps/search/?api=1&query=Via+Cesare+Battisti+36+80059+Torre+del+Greco+NA" 
                    target="_blank" 
                    rel="noopener noreferrer"
                    className="inline-flex items-center gap-2 px-6 py-3 bg-[#C9A962] text-black text-xs uppercase tracking-[0.15em] font-semibold hover:bg-white transition-colors"
                    data-testid="maps-link"
                  >
                    📍 Google Maps
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-32 bg-[#C9A962] relative overflow-hidden">
        <div className="absolute inset-0 opacity-10">
          <div className="absolute top-0 left-0 w-96 h-96 bg-white rounded-full blur-3xl -translate-x-1/2 -translate-y-1/2" />
          <div className="absolute bottom-0 right-0 w-96 h-96 bg-black rounded-full blur-3xl translate-x-1/2 translate-y-1/2" />
        </div>
        
        <div className="reveal max-w-4xl mx-auto px-6 text-center relative z-10">
          <h2 className="font-serif text-4xl md:text-6xl text-black mb-6">
            Pronto per il tuo<br />nuovo Look?
          </h2>
          <p className="text-black/70 mb-10 max-w-md mx-auto">
            Prenota ora il tuo appuntamento e scopri l'esperienza Seventeen Barber.
          </p>
          <button 
            onClick={() => navigate('/prenota')}
            className="px-12 py-5 bg-black text-white text-sm uppercase tracking-[0.25em] font-semibold hover:bg-white hover:text-black transition-all duration-300"
          >
            Prenota Ora
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-16 bg-[#0a0a0a] border-t border-neutral-900" data-testid="footer">
        <div className="max-w-6xl mx-auto px-6">
          <div className="flex flex-col md:flex-row items-center justify-between gap-8">
            <div className="flex items-center gap-4">
              <div className="w-12 h-12 border border-[#C9A962] rounded-full flex items-center justify-center">
                <Scissors className="w-5 h-5 text-[#C9A962]" />
              </div>
              <div>
                <p className="font-serif text-xl text-white">SEVENTEEN</p>
                <p className="text-[10px] uppercase tracking-[0.3em] text-[#C9A962]">Barber</p>
              </div>
            </div>
            
            <div className="flex items-center gap-8">
              <Link to="/prenota" className="text-neutral-500 hover:text-[#C9A962] text-sm uppercase tracking-wider transition-colors">Prenota</Link>
              <Link to="/gallery" className="text-neutral-500 hover:text-[#C9A962] text-sm uppercase tracking-wider transition-colors">Gallery</Link>
            </div>
            
            <p className="text-neutral-600 text-xs tracking-wider">
              © 2024 Seventeen Barber
            </p>
          </div>
        </div>
      </footer>
    </div>
  );
};

// Login Page with Phone and OTP
const LoginPage = () => {
  const { user, login } = useAuth();
  const { logoutAdmin } = useAdminAuth();
  const navigate = useNavigate();
  const [step, setStep] = useState(1); // 1: info, 2: otp
  const [formData, setFormData] = useState({
    name: "",
    email: "",
    phone: ""
  });
  const [otpCode, setOtpCode] = useState("");
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (user) {
      navigate('/prenota');
    }
  }, [user, navigate]);

  const handleRequestOTP = async (e) => {
    e.preventDefault();
    if (!formData.name || !formData.email || !formData.phone) {
      toast.error("Compila tutti i campi");
      return;
    }
    
    setLoading(true);
    try {
      await axios.post(`${API}/auth/request-otp`, {
        name: formData.name,
        email: formData.email,
        phone: formData.phone
      });
      toast.success("Codice inviato alla tua email!");
      setStep(2);
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nell'invio del codice");
    } finally {
      setLoading(false);
    }
  };

  const handleVerifyOTP = async (e) => {
    e.preventDefault();
    if (!otpCode || otpCode.length !== 6) {
      toast.error("Inserisci il codice a 6 cifre");
      return;
    }
    
    setLoading(true);
    try {
      await logoutAdmin();
      const res = await axios.post(`${API}/auth/verify-otp`, {
        email: formData.email,
        otp_code: otpCode
      });
      login(res.data.user);
      toast.success("Benvenuto!");
      navigate("/prenota");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Codice non valido");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-neutral-50" data-testid="login-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-12 shadow-xl">
          <div className="text-center mb-10">
            <div className="w-20 h-20 mx-auto mb-6 border border-[#C9A962] rounded-full flex items-center justify-center">
              <Scissors className="w-8 h-8 text-[#C9A962]" />
            </div>
            <div className="gold-line mx-auto mb-6" />
            <h1 className="font-serif text-3xl tracking-wide mb-2">
              {step === 1 ? "REGISTRATI" : "VERIFICA"}
            </h1>
            <p className="text-neutral-500 text-sm font-light">
              {step === 1 ? "Inserisci i tuoi dati per prenotare" : "Inserisci il codice ricevuto via email"}
            </p>
          </div>

          {step === 1 ? (
            <form onSubmit={handleRequestOTP} className="space-y-5">
              <div>
                <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">Nome e Cognome</label>
                <input
                  type="text"
                  placeholder="Mario Rossi"
                  className="input-luxury"
                  value={formData.name}
                  onChange={(e) => setFormData({...formData, name: e.target.value})}
                  required
                  data-testid="register-name-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">Email</label>
                <input
                  type="email"
                  placeholder="mario@email.com"
                  className="input-luxury"
                  value={formData.email}
                  onChange={(e) => setFormData({...formData, email: e.target.value})}
                  required
                  data-testid="register-email-input"
                />
              </div>
              <div>
                <label className="block text-xs uppercase tracking-wider text-neutral-500 mb-2">Numero di Telefono</label>
                <input
                  type="tel"
                  placeholder="+39 333 1234567"
                  className="input-luxury"
                  value={formData.phone}
                  onChange={(e) => setFormData({...formData, phone: e.target.value})}
                  required
                  data-testid="register-phone-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading}
                className="w-full btn-gold py-4 uppercase tracking-[0.2em] text-sm font-medium disabled:opacity-50 mt-4"
                data-testid="request-otp-btn"
              >
                {loading ? "Invio in corso..." : "Ricevi Codice"}
              </button>
            </form>
          ) : (
            <form onSubmit={handleVerifyOTP} className="space-y-6">
              <div className="text-center mb-4">
                <p className="text-sm text-neutral-500">
                  Codice inviato a <span className="text-[#C9A962] font-medium">{formData.email}</span>
                </p>
              </div>
              <div>
                <input
                  type="text"
                  placeholder="000000"
                  className="input-luxury text-center text-2xl tracking-[0.5em] font-mono"
                  value={otpCode}
                  onChange={(e) => setOtpCode(e.target.value.replace(/\D/g, '').slice(0, 6))}
                  maxLength={6}
                  required
                  data-testid="otp-input"
                />
              </div>
              <button
                type="submit"
                disabled={loading || otpCode.length !== 6}
                className="w-full btn-gold py-4 uppercase tracking-[0.2em] text-sm font-medium disabled:opacity-50"
                data-testid="verify-otp-btn"
              >
                {loading ? "Verifica..." : "Conferma"}
              </button>
              <button
                type="button"
                onClick={() => { setStep(1); setOtpCode(""); }}
                className="w-full text-center text-sm text-neutral-500 hover:text-[#C9A962] transition-colors"
              >
                ← Torna indietro
              </button>
            </form>
          )}

          <div className="relative py-6 mt-4">
            <div className="absolute inset-0 flex items-center">
              <div className="w-full luxury-divider"></div>
            </div>
            <div className="relative flex justify-center">
              <span className="bg-white px-4 text-xs uppercase tracking-[0.15em] text-neutral-400">admin</span>
            </div>
          </div>

          <Link
            to="/admin/login"
            className="block w-full text-center border border-neutral-200 py-4 text-xs uppercase tracking-[0.2em] hover:border-[#C9A962] hover:text-[#C9A962] transition-all font-medium"
            data-testid="admin-login-link"
          >
            Accesso Admin
          </Link>
        </div>
        
        <p className="text-center mt-8 text-xs text-neutral-400 tracking-wider">
          SEVENTEEN BARBER · Premium Grooming
        </p>
      </div>
    </div>
  );
};

// Admin Login Page
const AdminLoginPage = () => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const { loginAdmin, admin } = useAdminAuth();
  const { logout } = useAuth();
  const navigate = useNavigate();

  useEffect(() => {
    if (admin) {
      navigate('/admin');
    }
  }, [admin, navigate]);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      await logout();
      const res = await axios.post(`${API}/auth/admin/login`, { email, password });
      loginAdmin(res.data.user);
      toast.success("Benvenuto Admin!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Credenziali non valide");
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center px-6 bg-[#0a0a0a]" data-testid="admin-login-page">
      <div className="w-full max-w-md">
        <div className="bg-white p-12 shadow-2xl">
          <div className="text-center mb-10">
            <div className="w-20 h-20 mx-auto mb-6 bg-black rounded-full flex items-center justify-center">
              <Scissors className="w-8 h-8 text-[#C9A962]" />
            </div>
            <div className="gold-line mx-auto mb-6" />
            <h1 className="font-serif text-3xl tracking-wide mb-2">ADMIN</h1>
            <p className="text-neutral-500 text-sm font-light">Pannello Amministratore</p>
          </div>

          <form onSubmit={handleSubmit} className="space-y-6">
            <input
              type="email"
              placeholder="Email"
              className="input-luxury"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              required
              data-testid="admin-email-input"
            />
            <input
              type="password"
              placeholder="Password"
              className="input-luxury"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              required
              data-testid="admin-password-input"
            />
            <button
              type="submit"
              disabled={loading}
              className="w-full btn-gold py-4 uppercase tracking-[0.2em] text-sm font-medium disabled:opacity-50"
              data-testid="admin-login-btn"
            >
              {loading ? "Caricamento..." : "Accedi"}
            </button>
          </form>

          <div className="mt-8 text-center">
            <Link
              to="/login"
              className="text-xs uppercase tracking-[0.15em] text-neutral-400 hover:text-[#C9A962] transition-colors"
            >
              ← Torna al login utente
            </Link>
          </div>
        </div>
      </div>
    </div>
  );
};

// Booking Page
const BookingPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [barbers, setBarbers] = useState([]);
  const [selectedBarber, setSelectedBarber] = useState(null);
  const [services, setServices] = useState([]);
  const [selectedService, setSelectedService] = useState(null);
  const [selectedDate, setSelectedDate] = useState(null);
  const [availableSlots, setAvailableSlots] = useState([]);
  const [bookedSlots, setBookedSlots] = useState([]);
  const [selectedTime, setSelectedTime] = useState(null);
  const [loading, setLoading] = useState(false);
  const [step, setStep] = useState(1);
  const [vacationRanges, setVacationRanges] = useState([]);

  const currentUser = location.state?.user || user;

  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate("/login");
      return;
    }
    const fetchBarbers = async () => {
      try {
        const res = await axios.get(`${API}/barbers`);
        setBarbers(res.data);
        // Auto-select if only one active barber
        if (res.data.length === 1) {
          setSelectedBarber(res.data[0]);
        }
      } catch (e) { console.error(e); }
    };
    fetchBarbers();
  }, [currentUser, authLoading, navigate]);

  useEffect(() => {
    if (!selectedBarber) return;
    const fetchServices = async () => {
      try {
        const res = await axios.get(`${API}/services?barber_id=${selectedBarber.id}`);
        setServices(res.data);
        setSelectedService(null);
      } catch (e) { console.error(e); }
    };
    const fetchVacations = async () => {
      try {
        const res = await axios.get(`${API}/bookings/vacation-dates?barber_id=${selectedBarber.id}`);
        setVacationRanges(res.data || []);
      } catch (e) { setVacationRanges([]); }
    };
    fetchServices();
    fetchVacations();
  }, [selectedBarber]);

  useEffect(() => {
    if (selectedDate && selectedBarber) {
      fetchSlots();
    }
    // eslint-disable-next-line
  }, [selectedDate, selectedBarber]);

  const fetchSlots = async () => {
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      const res = await axios.get(`${API}/bookings/slots?date=${dateStr}&barber_id=${selectedBarber.id}`);
      setAvailableSlots(res.data.slots || []);
      setBookedSlots(res.data.booked_slots || []);
      setSelectedTime(null);
    } catch (e) { console.error(e); }
  };

  const handleBooking = async () => {
    if (!selectedBarber || !selectedService || !selectedDate || !selectedTime) {
      toast.error("Seleziona tutti i campi");
      return;
    }
    setLoading(true);
    try {
      const dateStr = format(selectedDate, "yyyy-MM-dd");
      await axios.post(`${API}/bookings`, {
        service_id: selectedService.id,
        barber_id: selectedBarber.id,
        date: dateStr,
        time: selectedTime
      });
      toast.success("Prenotazione confermata! Riceverai una email di conferma.");
      navigate("/dashboard");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Errore nella prenotazione");
    } finally {
      setLoading(false);
    }
  };

  const isDateDisabled = (date) => {
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    const maxDate = addDays(today, 14); // 2 weeks limit
    if (isMonday(date) || isSunday(date) || date < today || date > maxDate) return true;
    const dateStr = format(date, "yyyy-MM-dd");
    return vacationRanges.some(v => dateStr >= v.start_date && dateStr <= v.end_date);
  };

  const allSlots = [
    "09:00",
    "10:00", "10:30", "11:00", "11:30",
    "12:00", "12:30", "13:00",
    "14:00", "14:30", "15:00", "15:30",
    "16:00", "16:30", "17:00", "17:30",
    "18:00", "18:30", "19:00", "19:30"
  ];

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Scissors className="w-10 h-10 animate-pulse" />
      </div>
    );
  }

  if (!currentUser) return null;

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="booking-page">
      <div className="max-w-6xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">Ciao, {currentUser.name}</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">PRENOTAZIONE</h1>
        </div>

        {/* Steps Indicator */}
        <div className="flex items-center gap-4 mb-12">
          {[1, 2, 3].map((s) => (
            <div key={s} className="flex items-center gap-2">
              <div 
                className={`w-8 h-8 flex items-center justify-center text-sm font-bold ${
                  step >= s ? "bg-black text-white" : "bg-neutral-100 text-neutral-400"
                }`}
              >
                {s}
              </div>
              <span className={`text-xs uppercase tracking-[0.1em] ${step >= s ? "text-black" : "text-neutral-400"}`}>
                {s === 1 ? "Servizio" : s === 2 ? "Data" : "Ora"}
              </span>
              {s < 3 && <div className="w-12 h-px bg-neutral-200 ml-2" />}
            </div>
          ))}
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
          {/* Left Column - Selection */}
          <div className="lg:col-span-8">
            {/* Barber Selection (visible only if >1 active barbers) */}
            {barbers.length > 1 && (
              <div className="mb-8">
                <h3 className="text-xs uppercase tracking-[0.2em] mb-6">Scegli il barbiere</h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  {barbers.map((b) => (
                    <button
                      key={b.id}
                      onClick={() => setSelectedBarber(b)}
                      className={`p-4 border text-left transition-all ${
                        selectedBarber?.id === b.id ? "border-black bg-black text-white" : "border-neutral-200 hover:border-black"
                      }`}
                      data-testid={`barber-select-${b.id}`}
                    >
                      <div className="flex items-center gap-3">
                        {b.photo_url ? (
                          <img src={b.photo_url} alt={b.name} className="w-12 h-12 rounded-full object-cover border-2 border-[#C9A962]" />
                        ) : (
                          <div className="w-12 h-12 rounded-full bg-neutral-200 flex items-center justify-center border-2 border-[#C9A962]/30">
                            <Scissors className="w-5 h-5 text-[#C9A962]" />
                          </div>
                        )}
                        <div>
                          <h4 className="font-serif text-lg">{b.name}</h4>
                          {b.specialties?.[0] && <p className={`text-xs ${selectedBarber?.id === b.id ? "text-neutral-300" : "text-neutral-500"}`}>{b.specialties[0]}</p>}
                        </div>
                      </div>
                    </button>
                  ))}
                </div>
              </div>
            )}

            {/* Step 1: Service Selection */}
            <div className={step === 1 ? "" : "opacity-50"}>
              <h3 className="text-xs uppercase tracking-[0.2em] mb-6">1. Scegli il servizio</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
                {services.map((service) => (
                  <button
                    key={service.id}
                    onClick={() => {
                      setSelectedService(service);
                      setStep(2);
                    }}
                    className={`p-6 border text-left transition-all ${
                      selectedService?.id === service.id 
                        ? "border-black bg-black text-white" 
                        : "border-neutral-200 hover:border-black"
                    }`}
                    data-testid={`service-select-${service.name.toLowerCase().replace(/\s+/g, '-')}`}
                  >
                    <h4 className="font-serif text-xl mb-2">{service.name}</h4>
                    <p className={`text-sm ${selectedService?.id === service.id ? "text-neutral-300" : "text-neutral-500"}`}>
                      {service.duration} min • €{service.price}
                    </p>
                  </button>
                ))}
              </div>
            </div>

            {/* Step 2: Date Selection */}
            {selectedService && (
              <div className={step >= 2 ? "" : "opacity-50"}>
                <h3 className="text-xs uppercase tracking-[0.2em] mb-6">2. Scegli la data</h3>
                <div className="border border-neutral-200 p-6 mb-8 inline-block">
                  <Calendar
                    mode="single"
                    selected={selectedDate}
                    onSelect={(date) => {
                      setSelectedDate(date);
                      setStep(3);
                    }}
                    disabled={isDateDisabled}
                    locale={it}
                    fromDate={new Date()}
                    toDate={addDays(new Date(), 14)}
                    data-testid="booking-calendar"
                  />
                </div>
                <p className="text-xs text-neutral-500 mb-8">* Chiuso domenica e lunedì · Prenotazioni fino a 2 settimane</p>
              </div>
            )}

            {/* Step 3: Time Selection */}
            {selectedDate && (
              <div>
                <h3 className="text-xs uppercase tracking-[0.2em] mb-6">3. Scegli l'orario</h3>
                <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
                  {allSlots.map((slot) => {
                    const isBooked = bookedSlots.includes(slot);
                    const isSelected = selectedTime === slot;
                    
                    return (
                      <button
                        key={slot}
                        onClick={() => !isBooked && setSelectedTime(slot)}
                        disabled={isBooked}
                        className={`time-slot ${isSelected ? "selected" : ""} ${isBooked ? "booked" : ""}`}
                        data-testid={`time-slot-${slot.replace(":", "")}`}
                      >
                        {slot}
                      </button>
                    );
                  })}
                </div>
              </div>
            )}
          </div>

          {/* Right Column - Summary */}
          <div className="lg:col-span-4">
            <div className="sticky top-24 border border-neutral-200 p-8">
              <h3 className="text-xs uppercase tracking-[0.2em] mb-6">Riepilogo</h3>
              
              {selectedBarber && barbers.length > 1 && (
                <div className="mb-6 pb-6 border-b border-neutral-100">
                  <p className="text-xs text-neutral-500 uppercase tracking-[0.1em] mb-2">Barbiere</p>
                  <p className="font-serif text-xl">{selectedBarber.name}</p>
                </div>
              )}

              {selectedService && (
                <div className="mb-6 pb-6 border-b border-neutral-100">
                  <p className="text-xs text-neutral-500 uppercase tracking-[0.1em] mb-2">Servizio</p>
                  <p className="font-serif text-xl">{selectedService.name}</p>
                  <p className="text-neutral-500">€{selectedService.price}</p>
                </div>
              )}

              {selectedDate && (
                <div className="mb-6 pb-6 border-b border-neutral-100">
                  <p className="text-xs text-neutral-500 uppercase tracking-[0.1em] mb-2">Data</p>
                  <p className="font-serif text-xl">
                    {format(selectedDate, "d MMMM yyyy", { locale: it })}
                  </p>
                </div>
              )}

              {selectedTime && (
                <div className="mb-8">
                  <p className="text-xs text-neutral-500 uppercase tracking-[0.1em] mb-2">Ora</p>
                  <p className="font-serif text-xl">{selectedTime}</p>
                </div>
              )}

              <button
                onClick={handleBooking}
                disabled={!selectedService || !selectedDate || !selectedTime || loading}
                className="w-full bg-black text-white py-4 uppercase tracking-[0.2em] text-sm font-bold hover:bg-neutral-800 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                data-testid="confirm-booking-btn"
              >
                {loading ? "Prenotazione..." : "Conferma Prenotazione"}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

// User Dashboard
const DashboardPage = () => {
  const { user, loading: authLoading } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  const currentUser = location.state?.user || user;

  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate("/login");
      return;
    }
    if (currentUser) {
      fetchBookings();
    }
  }, [currentUser, authLoading, navigate]);

  const fetchBookings = async () => {
    try {
      const res = await axios.get(`${API}/bookings/my`);
      setBookings(res.data);
    } catch (e) {
      console.error("Error fetching bookings:", e);
    } finally {
      setLoading(false);
    }
  };

  const cancelBooking = async (bookingId) => {
    try {
      await axios.delete(`${API}/bookings/${bookingId}`);
      toast.success("Prenotazione cancellata");
      fetchBookings();
    } catch (e) {
      toast.error("Errore nella cancellazione");
    }
  };

  if (authLoading || !currentUser) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Scissors className="w-10 h-10 animate-pulse" />
      </div>
    );
  }

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="dashboard-page">
      <div className="max-w-4xl mx-auto px-6">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">Ciao, {currentUser.name}</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">LE MIE PRENOTAZIONI</h1>
        </div>

        {loading ? (
          <p className="text-neutral-500">Caricamento...</p>
        ) : bookings.length === 0 ? (
          <div className="text-center py-16 border border-neutral-200">
            <CalendarIcon className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-500 mb-6">Nessuna prenotazione</p>
            <button
              onClick={() => navigate("/prenota")}
              className="bg-black text-white px-8 py-3 uppercase tracking-[0.2em] text-sm font-bold hover:bg-neutral-800 transition-colors"
              data-testid="empty-book-btn"
            >
              Prenota Ora
            </button>
          </div>
        ) : (
          <div className="space-y-4">
            {bookings.map((booking) => (
              <div 
                key={booking.id} 
                className={`border p-6 flex flex-col md:flex-row md:items-center justify-between gap-4 ${
                  booking.status === "cancelled" ? "border-neutral-100 opacity-50" : "border-neutral-200"
                }`}
                data-testid={`booking-card-${booking.id}`}
              >
                <div>
                  <h3 className="font-serif text-xl mb-1">{booking.service_name}</h3>
                  <p className="text-neutral-500">
                    {booking.date} alle {booking.time}
                  </p>
                </div>
                <div className="flex items-center gap-4">
                  <span className={`text-xs uppercase tracking-[0.1em] px-3 py-1 ${
                    booking.status === "confirmed" ? "bg-black text-white" :
                    booking.status === "completed" ? "bg-neutral-100" :
                    "bg-neutral-100 text-neutral-400"
                  }`}>
                    {booking.status === "confirmed" ? "Confermata" :
                     booking.status === "completed" ? "Completata" : "Cancellata"}
                  </span>
                  {booking.status === "confirmed" && (
                    <button
                      onClick={() => cancelBooking(booking.id)}
                      className="text-xs uppercase tracking-[0.1em] text-red-600 hover:text-red-800"
                      data-testid={`cancel-booking-${booking.id}`}
                    >
                      Cancella
                    </button>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Gallery Page
const GalleryPage = () => {
  const [gallery, setGallery] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchGallery = async () => {
      try {
        const res = await axios.get(`${API}/gallery`);
        setGallery(res.data);
      } catch (e) {
        console.error("Error fetching gallery:", e);
      } finally {
        setLoading(false);
      }
    };
    fetchGallery();
  }, []);

  return (
    <div className="min-h-screen pt-24 pb-12" data-testid="gallery-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-12">
          <p className="text-xs uppercase tracking-[0.3em] text-neutral-500 mb-4">Il Nostro Lavoro</p>
          <h1 className="font-serif text-4xl md:text-5xl tracking-tight">GALLERY</h1>
        </div>

        {loading ? (
          <p className="text-neutral-500">Caricamento...</p>
        ) : gallery.length === 0 ? (
          <div className="text-center py-16 border border-neutral-200">
            <Image className="w-12 h-12 mx-auto mb-4 text-neutral-300" />
            <p className="text-neutral-500">Nessun contenuto nella gallery</p>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-1">
            {gallery.map((item, index) => (
              <div key={item.id} className="group relative aspect-square overflow-hidden" data-testid={`gallery-item-${index}`}>
                {item.media_type === "video" ? (
                  <video 
                    src={item.media_url} 
                    className="w-full h-full object-cover"
                    controls
                  />
                ) : (
                  <img 
                    src={item.media_url} 
                    alt={item.title}
                    className="w-full h-full object-cover gallery-image"
                  />
                )}
                <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-end p-6">
                  <div className="text-white">
                    <h3 className="font-serif text-xl mb-1">{item.title}</h3>
                    {item.description && (
                      <p className="text-sm text-neutral-300">{item.description}</p>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

// Barbers Manager (Admin Tab)
const BarbersManager = () => {
  const [barbers, setBarbers] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({
    name: "",
    email: "",
    password: "",
    photo_url: "",
    bio: "",
    specialties: ""
  });
  const [saving, setSaving] = useState(false);

  const fetchBarbers = async () => {
    try {
      const res = await axios.get(`${API}/admin/barbers`);
      setBarbers(res.data);
    } catch (e) {
      toast.error("Errore caricamento barbieri");
    }
  };

  useEffect(() => { fetchBarbers(); }, []);

  const handleSave = async () => {
    if (!form.name || !form.email) {
      toast.error("Nome ed email obbligatori");
      return;
    }
    setSaving(true);
    try {
      await axios.post(`${API}/admin/barbers`, {
        name: form.name,
        email: form.email,
        password: form.password || null,
        photo_url: form.photo_url,
        bio: form.bio,
        specialties: form.specialties.split(',').map(s => s.trim()).filter(Boolean)
      });
      toast.success("Barbiere aggiunto!");
      setForm({ name: "", email: "", password: "", photo_url: "", bio: "", specialties: "" });
      setShowForm(false);
      fetchBarbers();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    } finally {
      setSaving(false);
    }
  };

  const toggleActive = async (barber) => {
    try {
      await axios.patch(`${API}/admin/barbers/${barber.id}`, { is_active: !barber.is_active });
      toast.success(barber.is_active ? "Barbiere disattivato" : "Barbiere riattivato");
      fetchBarbers();
    } catch (e) {
      toast.error("Errore aggiornamento");
    }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 p-6 transition-all duration-500">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-serif text-2xl text-white">Gestione Barbieri</h3>
          <p className="text-xs text-neutral-500 mt-1">Aggiungi e gestisci i barbieri del salone</p>
        </div>
        <button
          onClick={() => setShowForm(!showForm)}
          className="flex items-center gap-2 px-5 py-2.5 bg-[#C9A962] text-black text-xs uppercase tracking-[0.15em] font-semibold hover:bg-white transition-all duration-300"
          data-testid="toggle-add-barber"
        >
          <Plus className="w-4 h-4" /> {showForm ? "Annulla" : "Aggiungi"}
        </button>
      </div>

      {/* Add Form */}
      {showForm && (
        <div className="mb-8 pb-8 border-b border-neutral-800 grid grid-cols-1 md:grid-cols-2 gap-4 animate-fade-in-up">
          <input
            type="text" placeholder="Nome completo"
            value={form.name}
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            className="bg-neutral-800 border border-neutral-700 px-4 py-3 text-white placeholder-neutral-500 focus:border-[#C9A962] outline-none transition-colors"
            data-testid="barber-name-input"
          />
          <input
            type="email" placeholder="Email"
            value={form.email}
            onChange={(e) => setForm({ ...form, email: e.target.value })}
            className="bg-neutral-800 border border-neutral-700 px-4 py-3 text-white placeholder-neutral-500 focus:border-[#C9A962] outline-none transition-colors"
            data-testid="barber-email-input"
          />
          <input
            type="password" placeholder="Password account admin (opzionale)"
            value={form.password}
            onChange={(e) => setForm({ ...form, password: e.target.value })}
            className="bg-neutral-800 border border-neutral-700 px-4 py-3 text-white placeholder-neutral-500 focus:border-[#C9A962] outline-none transition-colors"
            data-testid="barber-password-input"
          />
          <input
            type="text" placeholder="URL foto profilo (opzionale)"
            value={form.photo_url}
            onChange={(e) => setForm({ ...form, photo_url: e.target.value })}
            className="bg-neutral-800 border border-neutral-700 px-4 py-3 text-white placeholder-neutral-500 focus:border-[#C9A962] outline-none transition-colors"
            data-testid="barber-photo-input"
          />
          <input
            type="text" placeholder="Specialità (separate da virgola)"
            value={form.specialties}
            onChange={(e) => setForm({ ...form, specialties: e.target.value })}
            className="md:col-span-2 bg-neutral-800 border border-neutral-700 px-4 py-3 text-white placeholder-neutral-500 focus:border-[#C9A962] outline-none transition-colors"
            data-testid="barber-specialties-input"
          />
          <textarea
            placeholder="Bio breve (opzionale)"
            value={form.bio}
            onChange={(e) => setForm({ ...form, bio: e.target.value })}
            rows={3}
            className="md:col-span-2 bg-neutral-800 border border-neutral-700 px-4 py-3 text-white placeholder-neutral-500 focus:border-[#C9A962] outline-none transition-colors resize-none"
            data-testid="barber-bio-input"
          />
          <button
            onClick={handleSave}
            disabled={saving}
            className="md:col-span-2 py-3 bg-[#C9A962] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all duration-300 disabled:opacity-50"
            data-testid="save-barber-btn"
          >
            {saving ? "Salvataggio..." : "Salva Barbiere"}
          </button>
          <p className="md:col-span-2 text-xs text-neutral-500">
            Se imposti una password, il barbiere potrà accedere come admin con la sua email.
          </p>
        </div>
      )}

      {/* List */}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {barbers.map(b => (
          <div
            key={b.id}
            className={`p-5 border transition-all duration-300 hover:translate-y-[-2px] ${
              b.is_active ? 'bg-neutral-800/50 border-neutral-700 hover:border-[#C9A962]/50' : 'bg-neutral-900/50 border-neutral-800 opacity-60'
            }`}
            data-testid={`barber-card-${b.id}`}
          >
            <div className="flex gap-4">
              {b.photo_url ? (
                <img src={b.photo_url} alt={b.name} className="w-16 h-16 rounded-full object-cover border-2 border-[#C9A962]" />
              ) : (
                <div className="w-16 h-16 rounded-full bg-neutral-700 flex items-center justify-center border-2 border-[#C9A962]/30">
                  <Scissors className="w-6 h-6 text-[#C9A962]" />
                </div>
              )}
              <div className="flex-1 min-w-0">
                <h4 className="text-white font-serif text-lg truncate">{b.name}</h4>
                <p className="text-neutral-500 text-xs truncate">{b.email}</p>
                {b.specialties && b.specialties.length > 0 && (
                  <div className="flex flex-wrap gap-1 mt-2">
                    {b.specialties.map((s, i) => (
                      <span key={`${b.id}-spec-${s}-${i}`} className="text-[10px] uppercase tracking-wider bg-[#C9A962]/10 text-[#C9A962] px-2 py-0.5">{s}</span>
                    ))}
                  </div>
                )}
              </div>
            </div>
            {b.bio && <p className="text-neutral-400 text-sm mt-3 leading-relaxed">{b.bio}</p>}
            <div className="flex items-center justify-between mt-4 pt-3 border-t border-neutral-700">
              <span className={`text-[10px] uppercase tracking-wider ${b.is_active ? 'text-green-400' : 'text-neutral-500'}`}>
                {b.is_active ? '● Attivo' : '○ Inattivo'}
              </span>
              <button
                onClick={() => toggleActive(b)}
                className="text-xs text-neutral-400 hover:text-[#C9A962] uppercase tracking-wider transition-colors"
                data-testid={`toggle-barber-${b.id}`}
              >
                {b.is_active ? 'Disattiva' : 'Riattiva'}
              </button>
            </div>
          </div>
        ))}
        {barbers.length === 0 && (
          <p className="md:col-span-2 text-center py-12 text-neutral-500">Nessun barbiere ancora registrato.</p>
        )}
      </div>
    </div>
  );
};

// Services Manager (Admin Tab)
const ServicesManager = ({ adminInfo }) => {
  const [barbers, setBarbers] = useState([]);
  const [services, setServices] = useState([]);
  const [selectedBarberId, setSelectedBarberId] = useState(adminInfo?.barber_id || "");
  const [form, setForm] = useState({ name: "", duration: 30, price: 0 });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);

  const isSuper = adminInfo?.is_super_admin;

  const fetchData = async () => {
    try {
      if (isSuper) {
        const bres = await axios.get(`${API}/admin/barbers`);
        setBarbers(bres.data);
        if (!selectedBarberId && bres.data[0]) setSelectedBarberId(bres.data[0].id);
      }
      const sid = selectedBarberId || adminInfo?.barber_id;
      if (sid) {
        const sres = await axios.get(`${API}/services?barber_id=${sid}`);
        setServices(sres.data);
      }
    } catch (e) {
      toast.error("Errore caricamento servizi");
    }
  };

  useEffect(() => { fetchData(); /* eslint-disable-next-line */ }, [selectedBarberId]);

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.error("Nome e prezzo obbligatori");
    try {
      if (editing) {
        await axios.patch(`${API}/admin/services/${editing.id}`, form);
        toast.success("Servizio aggiornato");
      } else {
        await axios.post(`${API}/admin/services`, { ...form, barber_id: selectedBarberId });
        toast.success("Servizio creato");
      }
      setForm({ name: "", duration: 30, price: 0 });
      setEditing(null);
      setShowForm(false);
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  const startEdit = (svc) => {
    setEditing(svc);
    setForm({ name: svc.name, duration: svc.duration, price: svc.price });
    setShowForm(true);
  };

  const handleDelete = async (svc) => {
    if (!window.confirm(`Eliminare "${svc.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/services/${svc.id}`);
      toast.success("Servizio eliminato");
      fetchData();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore");
    }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-serif text-2xl text-white">Servizi & Prezzi</h3>
          <p className="text-xs text-neutral-500 mt-1">Ogni barbiere gestisce i propri servizi e prezzi</p>
        </div>
        <div className="flex items-center gap-3">
          {isSuper && barbers.length > 0 && (
            <select
              value={selectedBarberId}
              onChange={(e) => setSelectedBarberId(e.target.value)}
              className="bg-neutral-800 border border-neutral-700 text-white px-3 py-2 text-sm focus:border-[#C9A962] outline-none"
              data-testid="services-barber-select"
            >
              {barbers.map(b => <option key={b.id} value={b.id}>{b.name}</option>)}
            </select>
          )}
          <button
            onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: "", duration: 30, price: 0 }); }}
            className="flex items-center gap-2 px-5 py-2.5 bg-[#C9A962] text-black text-xs uppercase tracking-[0.15em] font-semibold hover:bg-white transition-all duration-300"
            data-testid="toggle-add-service"
          >
            <Plus className="w-4 h-4" /> {showForm ? "Annulla" : "Aggiungi"}
          </button>
        </div>
      </div>

      {showForm && (
        <div className="mb-6 pb-6 border-b border-neutral-800 grid grid-cols-1 md:grid-cols-3 gap-3 animate-fade-in-up">
          <input type="text" placeholder="Nome servizio" value={form.name}
            onChange={(e) => setForm({...form, name: e.target.value})}
            className="bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
            data-testid="service-name-input" />
          <input type="number" placeholder="Durata (min)" value={form.duration}
            onChange={(e) => setForm({...form, duration: parseInt(e.target.value) || 30})}
            className="bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
            data-testid="service-duration-input" />
          <input type="number" step="0.5" placeholder="Prezzo €" value={form.price}
            onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})}
            className="bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
            data-testid="service-price-input" />
          <button onClick={handleSave}
            className="md:col-span-3 py-3 bg-[#C9A962] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all"
            data-testid="save-service-btn">
            {editing ? "Salva modifiche" : "Crea servizio"}
          </button>
        </div>
      )}

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        {services.map(svc => (
          <div key={svc.id} className="p-5 bg-neutral-800/50 border border-neutral-700 hover:border-[#C9A962]/50 transition-all" data-testid={`service-card-${svc.id}`}>
            <div className="flex items-start justify-between mb-3">
              <div>
                <h4 className="font-serif text-lg text-white">{svc.name}</h4>
                <p className="text-xs text-neutral-500 mt-1">{svc.duration} min</p>
              </div>
              <span className="font-serif text-2xl text-[#C9A962]">€{svc.price}</span>
            </div>
            <div className="flex gap-2 pt-3 border-t border-neutral-700">
              <button onClick={() => startEdit(svc)} className="flex-1 text-xs uppercase tracking-wider text-neutral-400 hover:text-[#C9A962] transition-colors flex items-center gap-1" data-testid={`edit-service-${svc.id}`}>
                <Edit3 className="w-3 h-3" /> Modifica
              </button>
              <button onClick={() => handleDelete(svc)} className="flex-1 text-xs uppercase tracking-wider text-neutral-400 hover:text-red-400 transition-colors flex items-center gap-1 justify-end" data-testid={`delete-service-${svc.id}`}>
                <Trash2 className="w-3 h-3" /> Elimina
              </button>
            </div>
          </div>
        ))}
        {services.length === 0 && (
          <p className="md:col-span-3 text-center py-12 text-neutral-500">Nessun servizio. Aggiungi il primo!</p>
        )}
      </div>
    </div>
  );
};

// Products Manager (Admin Tab)
const ProductsManager = () => {
  const [products, setProducts] = useState([]);
  const [requests, setRequests] = useState([]);
  const [form, setForm] = useState({ name: "", description: "", price: 0, photo_url: "" });
  const [showForm, setShowForm] = useState(false);
  const [editing, setEditing] = useState(null);
  const [view, setView] = useState("products");

  const fetchAll = async () => {
    try {
      const [p, r] = await Promise.all([
        axios.get(`${API}/admin/products`),
        axios.get(`${API}/admin/product-requests`)
      ]);
      setProducts(p.data);
      setRequests(r.data);
    } catch (e) {
      toast.error("Errore caricamento");
    }
  };
  useEffect(() => { fetchAll(); }, []);

  const handleSave = async () => {
    if (!form.name || !form.price) return toast.error("Nome e prezzo obbligatori");
    try {
      if (editing) {
        await axios.patch(`${API}/admin/products/${editing.id}`, form);
        toast.success("Prodotto aggiornato");
      } else {
        await axios.post(`${API}/admin/products`, form);
        toast.success("Prodotto creato");
      }
      setForm({ name: "", description: "", price: 0, photo_url: "" });
      setEditing(null);
      setShowForm(false);
      fetchAll();
    } catch (e) {
      toast.error("Errore");
    }
  };

  const startEdit = (p) => {
    setEditing(p);
    setForm({ name: p.name, description: p.description, price: p.price, photo_url: p.photo_url });
    setShowForm(true);
  };

  const toggleAvailable = async (p) => {
    try {
      await axios.patch(`${API}/admin/products/${p.id}`, { is_available: !p.is_available });
      fetchAll();
    } catch (e) { toast.error("Errore"); }
  };

  const handleDelete = async (p) => {
    if (!window.confirm(`Eliminare "${p.name}"?`)) return;
    try {
      await axios.delete(`${API}/admin/products/${p.id}`);
      toast.success("Prodotto eliminato");
      fetchAll();
    } catch (e) { toast.error("Errore"); }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 p-6">
      <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
        <div>
          <h3 className="font-serif text-2xl text-white">Prodotti del Salone</h3>
          <p className="text-xs text-neutral-500 mt-1">I clienti possono richiedere un prodotto tramite il sito</p>
        </div>
        <div className="flex items-center gap-2">
          <button onClick={() => setView("products")} className={`px-4 py-2 text-xs uppercase tracking-[0.1em] ${view==="products"?"bg-[#C9A962] text-black":"bg-neutral-800 text-neutral-400"} transition-all`} data-testid="view-products">Prodotti</button>
          <button onClick={() => setView("requests")} className={`px-4 py-2 text-xs uppercase tracking-[0.1em] ${view==="requests"?"bg-[#C9A962] text-black":"bg-neutral-800 text-neutral-400"} transition-all`} data-testid="view-requests">Richieste ({requests.length})</button>
          {view === "products" && (
            <button onClick={() => { setShowForm(!showForm); setEditing(null); setForm({ name: "", description: "", price: 0, photo_url: "" }); }}
              className="flex items-center gap-2 px-5 py-2.5 bg-[#C9A962] text-black text-xs uppercase tracking-[0.15em] font-semibold hover:bg-white transition-all"
              data-testid="toggle-add-product">
              <Plus className="w-4 h-4" /> {showForm ? "Annulla" : "Aggiungi"}
            </button>
          )}
        </div>
      </div>

      {view === "products" ? (
        <>
          {showForm && (
            <div className="mb-6 pb-6 border-b border-neutral-800 grid grid-cols-1 md:grid-cols-2 gap-3 animate-fade-in-up">
              <input type="text" placeholder="Nome prodotto" value={form.name}
                onChange={(e) => setForm({...form, name: e.target.value})}
                className="bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
                data-testid="product-name-input" />
              <input type="number" step="0.5" placeholder="Prezzo €" value={form.price}
                onChange={(e) => setForm({...form, price: parseFloat(e.target.value) || 0})}
                className="bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
                data-testid="product-price-input" />
              <input type="text" placeholder="URL foto (opzionale)" value={form.photo_url}
                onChange={(e) => setForm({...form, photo_url: e.target.value})}
                className="md:col-span-2 bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
                data-testid="product-photo-input" />
              <textarea placeholder="Descrizione" value={form.description}
                onChange={(e) => setForm({...form, description: e.target.value})}
                rows={3}
                className="md:col-span-2 bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none resize-none"
                data-testid="product-description-input" />
              <button onClick={handleSave}
                className="md:col-span-2 py-3 bg-[#C9A962] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all"
                data-testid="save-product-btn">
                {editing ? "Salva modifiche" : "Crea prodotto"}
              </button>
            </div>
          )}

          <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
            {products.map(p => (
              <div key={p.id} className={`p-4 bg-neutral-800/50 border transition-all ${p.is_available ? 'border-neutral-700 hover:border-[#C9A962]/50' : 'border-neutral-800 opacity-60'}`} data-testid={`product-card-${p.id}`}>
                {p.photo_url ? (
                  <img src={p.photo_url} alt={p.name} className="w-full h-40 object-cover mb-3" loading="lazy" />
                ) : (
                  <div className="w-full h-40 bg-neutral-700 flex items-center justify-center mb-3">
                    <Package className="w-10 h-10 text-[#C9A962]/50" />
                  </div>
                )}
                <div className="flex items-start justify-between mb-2">
                  <h4 className="font-serif text-lg text-white">{p.name}</h4>
                  <span className="font-serif text-lg text-[#C9A962]">€{p.price}</span>
                </div>
                {p.description && <p className="text-neutral-400 text-sm mb-3">{p.description}</p>}
                <div className="flex gap-1 pt-2 border-t border-neutral-700 text-[10px] uppercase tracking-wider">
                  <button onClick={() => toggleAvailable(p)} className="flex-1 text-neutral-400 hover:text-[#C9A962]" data-testid={`toggle-product-${p.id}`}>
                    {p.is_available ? 'Disattiva' : 'Attiva'}
                  </button>
                  <button onClick={() => startEdit(p)} className="flex-1 text-neutral-400 hover:text-[#C9A962]" data-testid={`edit-product-${p.id}`}>Modifica</button>
                  <button onClick={() => handleDelete(p)} className="flex-1 text-neutral-400 hover:text-red-400" data-testid={`delete-product-${p.id}`}>Elimina</button>
                </div>
              </div>
            ))}
            {products.length === 0 && (
              <p className="md:col-span-3 text-center py-12 text-neutral-500">Nessun prodotto. Aggiungi il primo!</p>
            )}
          </div>
        </>
      ) : (
        <div className="space-y-3">
          {requests.map(r => (
            <div key={r.id} className="p-4 bg-neutral-800/50 border border-neutral-700" data-testid={`request-${r.id}`}>
              <div className="flex items-center justify-between mb-2">
                <h4 className="text-white font-serif text-lg">{r.product_name}</h4>
                <span className="text-xs text-neutral-500">{new Date(r.created_at).toLocaleString('it-IT')}</span>
              </div>
              <p className="text-neutral-300 text-sm">{r.customer_name} · {r.customer_email} · {r.customer_phone || '—'}</p>
              {r.notes && <p className="text-neutral-500 text-sm mt-2 italic">"{r.notes}"</p>}
            </div>
          ))}
          {requests.length === 0 && <p className="text-center py-12 text-neutral-500">Nessuna richiesta prodotto.</p>}
        </div>
      )}
    </div>
  );
};

// Vacation Manager (Admin Tab) - close shop for a period
const VacationManager = () => {
  const [start, setStart] = useState("");
  const [end, setEnd] = useState("");
  const [reason, setReason] = useState("");
  const [vacations, setVacations] = useState([]);
  const [loading, setLoading] = useState(false);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [deleteId, setDeleteId] = useState(null);
  const [deleting, setDeleting] = useState(false);

  const fetchVacations = async () => {
    try {
      const res = await axios.get(`${API}/admin/vacations`);
      setVacations(res.data);
    } catch (e) { /* noop */ }
  };
  useEffect(() => { fetchVacations(); }, []);

  const openConfirm = () => {
    if (!start || !end) return toast.error("Seleziona data inizio e fine");
    if (end < start) return toast.error("La data di fine deve essere successiva o uguale a quella di inizio");
    setConfirmOpen(true);
  };

  const handleSubmit = async () => {
    setConfirmOpen(false);
    setLoading(true);
    try {
      const res = await axios.post(`${API}/admin/vacation`, { start_date: start, end_date: end, reason });
      toast.success(`Ferie registrate. Cancellate ${res.data.cancelled_count} prenotazioni.`);
      setStart(""); setEnd(""); setReason("");
      fetchVacations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore durante il salvataggio");
    } finally {
      setLoading(false);
    }
  };

  const handleDelete = async () => {
    if (!deleteId) return;
    setDeleting(true);
    try {
      await axios.delete(`${API}/admin/vacation/${deleteId}`);
      toast.success("Ferie eliminate. I clienti già avvisati non riceveranno nuove email.");
      setDeleteId(null);
      fetchVacations();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Errore eliminazione");
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 p-6">
      <div className="mb-6">
        <h3 className="font-serif text-2xl text-white">Ferie / Chiusura</h3>
        <p className="text-xs text-neutral-500 mt-1">Imposta un periodo di chiusura. Tutti i clienti prenotati in quei giorni riceveranno un'email di cancellazione.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6 pb-6 border-b border-neutral-800">
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Data inizio</label>
          <input type="date" value={start} onChange={(e) => setStart(e.target.value)} min={new Date().toISOString().split('T')[0]}
            className="w-full bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
            data-testid="vacation-start-input" />
        </div>
        <div>
          <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Data fine (inclusa)</label>
          <input type="date" value={end} onChange={(e) => setEnd(e.target.value)} min={start || new Date().toISOString().split('T')[0]}
            className="w-full bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
            data-testid="vacation-end-input" />
        </div>
        <textarea placeholder="Motivo (opzionale) — es. Ferie estive, Malattia..." value={reason} onChange={(e) => setReason(e.target.value)}
          rows={2}
          className="md:col-span-2 bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none resize-none"
          data-testid="vacation-reason-input" />
        <button onClick={openConfirm} disabled={loading || !start || !end}
          className="md:col-span-2 py-3 bg-[#C9A962] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all disabled:opacity-50 disabled:cursor-not-allowed"
          data-testid="save-vacation-btn">
          {loading ? "Cancellazione..." : "Chiudi Negozio & Notifica Clienti"}
        </button>
      </div>

      <AlertDialog open={confirmOpen} onOpenChange={setConfirmOpen}>
        <AlertDialogContent className="bg-neutral-900 border border-neutral-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-white">Confermare la chiusura?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400 text-sm">
              Tutte le prenotazioni confermate dal <span className="text-[#C9A962] font-semibold">{start}</span> al <span className="text-[#C9A962] font-semibold">{end}</span> verranno cancellate e i clienti riceveranno un'email di notifica. Questa azione non può essere annullata.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-neutral-800 border-neutral-700 text-white hover:bg-neutral-700" data-testid="vacation-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleSubmit} className="bg-[#C9A962] text-black hover:bg-white" data-testid="vacation-confirm-btn">Conferma & Notifica</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      <h4 className="text-xs uppercase tracking-[0.3em] text-[#C9A962] mb-4">Storico Chiusure</h4>
      <div className="space-y-2">
        {vacations.map(v => (
          <div key={v.id} className="p-4 bg-neutral-800/50 border border-neutral-700 flex items-center justify-between gap-3" data-testid={`vacation-${v.id}`}>
            <div className="flex-1 min-w-0">
              <p className="text-white text-sm">Dal {v.start_date} al {v.end_date}</p>
              {v.reason && <p className="text-neutral-500 text-xs italic mt-1">"{v.reason}"</p>}
            </div>
            <span className="text-xs text-[#C9A962] whitespace-nowrap">{v.cancelled_count} prenot. cancellate</span>
            <button onClick={() => setDeleteId(v.id)}
              className="text-xs uppercase tracking-[0.15em] text-neutral-400 hover:text-red-400 border border-neutral-700 hover:border-red-500 px-3 py-2 transition-colors"
              data-testid={`delete-vacation-${v.id}`}>
              Elimina
            </button>
          </div>
        ))}
        {vacations.length === 0 && <p className="text-center py-8 text-neutral-500 text-sm">Nessuna chiusura registrata.</p>}
      </div>

      <AlertDialog open={!!deleteId} onOpenChange={(o) => !o && setDeleteId(null)}>
        <AlertDialogContent className="bg-neutral-900 border border-neutral-800 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-serif text-2xl text-white">Eliminare queste ferie?</AlertDialogTitle>
            <AlertDialogDescription className="text-neutral-400 text-sm">
              La chiusura verrà rimossa e i clienti potranno di nuovo prenotare in quelle date. Le prenotazioni già cancellate rimangono cancellate (i clienti sono stati avvisati). Sei sicuro?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-neutral-800 border-neutral-700 text-white hover:bg-neutral-700" data-testid="delete-vacation-cancel-btn">Annulla</AlertDialogCancel>
            <AlertDialogAction onClick={handleDelete} disabled={deleting} className="bg-red-600 text-white hover:bg-red-500" data-testid="delete-vacation-confirm-btn">
              {deleting ? "Eliminazione..." : "Sì, elimina"}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
};

// Settings Manager (Admin Tab) - Site customization
const QRCodeManager = () => {
  const [target, setTarget] = useState("booking");
  const canvasRef = React.useRef(null);
  const origin = typeof window !== "undefined" ? window.location.origin : "";
  const targets = {
    booking: { path: "/prenota", label: "Pagina Prenotazioni", desc: "Il cliente atterra direttamente sul flusso di prenotazione." },
    home: { path: "/", label: "Home Page", desc: "Il cliente vede la home con servizi, gallery e contatti." },
  };
  const url = `${origin}${targets[target].path}`;

  const download = () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const link = document.createElement("a");
    link.download = `seventeen-barber-qr-${target}.png`;
    link.href = canvas.toDataURL("image/png");
    link.click();
  };

  const printQR = () => {
    const canvas = canvasRef.current?.querySelector("canvas");
    if (!canvas) return;
    const dataUrl = canvas.toDataURL("image/png");
    const w = window.open("", "_blank");
    if (!w) return toast.error("Consenti i popup per stampare");
    const doc = w.document;
    doc.title = "QR Code — Seventeen Barber";
    const style = doc.createElement("style");
    style.textContent = "body{font-family:Georgia,serif;text-align:center;padding:40px;background:#000;color:#fff;}h1{font-size:32px;letter-spacing:6px;margin-bottom:8px;color:#C9A962;}p{color:#999;margin-top:0;font-size:14px;letter-spacing:2px;text-transform:uppercase;}img{margin:40px auto;display:block;background:#fff;padding:24px;}.cta{font-size:22px;margin-top:20px;letter-spacing:3px;}";
    doc.head.appendChild(style);
    const h1 = doc.createElement("h1"); h1.textContent = "SEVENTEEN BARBER";
    const p = doc.createElement("p"); p.textContent = "Scannerizza per prenotare";
    const img = doc.createElement("img"); img.src = dataUrl; img.width = 360; img.height = 360;
    const cta = doc.createElement("div"); cta.className = "cta"; cta.textContent = "Prenota il tuo appuntamento";
    doc.body.append(h1, p, img, cta);
    setTimeout(() => { w.print(); }, 300);
  };

  const copyUrl = async () => {
    try {
      await navigator.clipboard.writeText(url);
      toast.success("Link copiato!");
    } catch (e) {
      toast.error("Impossibile copiare");
    }
  };

  return (
    <div className="bg-neutral-900 border border-neutral-800 p-6">
      <div className="mb-6">
        <h3 className="font-serif text-2xl text-white">QR Code del Salone</h3>
        <p className="text-xs text-neutral-500 mt-1">Stampa il QR per la vetrina, allo specchio o sul biglietto da visita. I clienti scansionano e prenotano in pochi tap.</p>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <div className="flex flex-col items-center justify-center bg-black border border-neutral-800 p-8" ref={canvasRef} data-testid="qr-preview">
          <QRCodeCanvas value={url} size={280} bgColor="#000000" fgColor="#C9A962" level="H" includeMargin={true} />
          <p className="text-neutral-500 text-xs uppercase tracking-[0.2em] mt-6">Anteprima</p>
        </div>

        <div className="space-y-6">
          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Destinazione</label>
            <div className="grid grid-cols-2 gap-2">
              {Object.entries(targets).map(([k, t]) => (
                <button key={k} onClick={() => setTarget(k)}
                  className={`px-3 py-3 text-xs uppercase tracking-[0.15em] transition-all border ${target === k ? 'bg-[#C9A962] text-black border-[#C9A962]' : 'bg-neutral-800 text-white border-neutral-700 hover:border-[#C9A962]'}`}
                  data-testid={`qr-target-${k}`}>
                  {t.label}
                </button>
              ))}
            </div>
            <p className="text-neutral-500 text-xs mt-2 italic">{targets[target].desc}</p>
          </div>

          <div>
            <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">Link codificato</label>
            <div className="flex gap-2">
              <input type="text" readOnly value={url}
                className="flex-1 bg-neutral-800 border border-neutral-700 text-white px-4 py-3 text-sm outline-none"
                data-testid="qr-url-input" />
              <button onClick={copyUrl}
                className="px-4 bg-neutral-800 border border-neutral-700 text-white hover:border-[#C9A962] text-xs uppercase tracking-[0.15em]"
                data-testid="qr-copy-btn">
                Copia
              </button>
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 pt-2">
            <button onClick={download}
              className="py-3 bg-[#C9A962] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all"
              data-testid="qr-download-btn">
              Scarica PNG
            </button>
            <button onClick={printQR}
              className="py-3 border border-[#C9A962] text-[#C9A962] text-xs uppercase tracking-[0.2em] font-semibold hover:bg-[#C9A962] hover:text-black transition-all"
              data-testid="qr-print-btn">
              Stampa
            </button>
          </div>

          <div className="pt-4 border-t border-neutral-800 text-xs text-neutral-500 leading-relaxed">
            <p className="uppercase tracking-[0.15em] text-[#C9A962] mb-2">Dove usarlo</p>
            <ul className="space-y-1 list-disc list-inside">
              <li>Vetrina del salone / specchio</li>
              <li>Bio Instagram (aggiungi il link nel campo "Sito web")</li>
              <li>WhatsApp Business</li>
              <li>Biglietti da visita e volantini</li>
              <li>Google Business Profile</li>
            </ul>
          </div>
        </div>
      </div>
    </div>
  );
};

// Settings Manager (Admin Tab) - Site customization
const SettingsManager = () => {
  const { settings, refresh } = useSettings();
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);

  useEffect(() => { setForm({ ...settings }); }, [settings]);

  const handleChange = (key, value) => setForm({ ...form, [key]: value });

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.patch(`${API}/admin/settings`, { values: form });
      toast.success("Impostazioni salvate");
      await refresh();
    } catch (e) {
      toast.error("Errore salvataggio");
    } finally {
      setSaving(false);
    }
  };

  const field = (key, label, type = "text", multiline = false) => (
    <div>
      <label className="block text-xs uppercase tracking-[0.15em] text-neutral-500 mb-2">{label}</label>
      {multiline ? (
        <textarea rows={3} value={form[key] || ""} onChange={(e) => handleChange(key, e.target.value)}
          className="w-full bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none resize-none"
          data-testid={`settings-${key}`} />
      ) : type === "color" ? (
        <div className="flex items-center gap-3">
          <input type="color" value={form[key] || "#C9A962"} onChange={(e) => handleChange(key, e.target.value)}
            className="w-16 h-12 bg-transparent border border-neutral-700 cursor-pointer"
            data-testid={`settings-${key}`} />
          <input type="text" value={form[key] || ""} onChange={(e) => handleChange(key, e.target.value)}
            className="flex-1 bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none" />
        </div>
      ) : type === "toggle" ? (
        <button onClick={() => handleChange(key, !form[key])}
          className={`px-4 py-2 text-xs uppercase tracking-wider transition-all ${form[key] ? 'bg-[#C9A962] text-black' : 'bg-neutral-800 text-neutral-500'}`}
          data-testid={`settings-${key}`}>
          {form[key] ? 'ATTIVO' : 'DISATTIVO'}
        </button>
      ) : (
        <input type={type} value={form[key] || ""} onChange={(e) => handleChange(key, e.target.value)}
          className="w-full bg-neutral-800 border border-neutral-700 text-white px-4 py-3 focus:border-[#C9A962] outline-none"
          data-testid={`settings-${key}`} />
      )}
    </div>
  );

  return (
    <div className="bg-neutral-900 border border-neutral-800 p-6">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h3 className="font-serif text-2xl text-white">Personalizza il Sito</h3>
          <p className="text-xs text-neutral-500 mt-1">Modifica testi, contatti e colori del salone</p>
        </div>
        <button onClick={handleSave} disabled={saving}
          className="px-6 py-3 bg-[#C9A962] text-black text-xs uppercase tracking-[0.2em] font-semibold hover:bg-white transition-all disabled:opacity-50"
          data-testid="save-settings-btn">
          {saving ? "Salvataggio..." : "Salva tutto"}
        </button>
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
        <section className="space-y-4">
          <h4 className="text-xs uppercase tracking-[0.3em] text-[#C9A962]">Identità</h4>
          {field("salon_name", "Nome Salone")}
          {field("salon_tagline", "Sottotitolo")}
          {field("hero_title", "Titolo Hero (usa <br/> per andare a capo)", "text", true)}
          {field("hero_subtitle", "Sottotitolo Hero")}
          {field("about_text", "Descrizione Servizi", "text", true)}
        </section>

        <section className="space-y-4">
          <h4 className="text-xs uppercase tracking-[0.3em] text-[#C9A962]">Contatti</h4>
          {field("contact_phone", "Telefono")}
          {field("contact_email", "Email pubblica")}
          {field("contact_address", "Indirizzo")}
          {field("maps_url", "Link Google Maps")}
        </section>

        <section className="space-y-4">
          <h4 className="text-xs uppercase tracking-[0.3em] text-[#C9A962]">Palette Colori</h4>
          {field("color_primary", "Colore Accento (oro)", "color")}
          {field("color_bg", "Colore Sfondo", "color")}
          {field("color_text", "Colore Testo", "color")}
        </section>

        <section className="space-y-4">
          <h4 className="text-xs uppercase tracking-[0.3em] text-[#C9A962]">Sezioni Sito</h4>
          <div className="flex items-center justify-between p-3 bg-neutral-800/50">
            <span className="text-white text-sm">Mostra Gallery</span>
            {field("show_gallery", "", "toggle")}
          </div>
          <div className="flex items-center justify-between p-3 bg-neutral-800/50">
            <span className="text-white text-sm">Mostra Team</span>
            {field("show_team", "", "toggle")}
          </div>
          <div className="flex items-center justify-between p-3 bg-neutral-800/50">
            <span className="text-white text-sm">Mostra Prodotti</span>
            {field("show_products", "", "toggle")}
          </div>
        </section>
      </div>
    </div>
  );
};

// Admin Dashboard with Calendar
const AdminPage = () => {
  const { admin, loading: authLoading } = useAdminAuth();
  const navigate = useNavigate();
  const [stats, setStats] = useState(null);
  const [bookings, setBookings] = useState([]);
  const [gallery, setGallery] = useState([]);
  const [currentMonth, setCurrentMonth] = useState(new Date());
  const [selectedDay, setSelectedDay] = useState(null);
  const [dayBookings, setDayBookings] = useState([]);
  const [newGalleryItem, setNewGalleryItem] = useState({
    title: "",
    description: "",
    media_url: "",
    media_type: "image"
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!authLoading && !admin) {
      navigate("/admin/login");
      return;
    }
    if (admin) {
      fetchData();
    }
  }, [admin, authLoading, navigate]);

  useEffect(() => {
    if (admin) {
      fetchCalendarBookings();
    }
  }, [currentMonth, admin]);

  const fetchData = async () => {
    try {
      const [statsRes, galleryRes] = await Promise.all([
        axios.get(`${API}/admin/stats`),
        axios.get(`${API}/gallery`)
      ]);
      setStats(statsRes.data);
      setGallery(galleryRes.data);
    } catch (e) {
      console.error("Error fetching admin data:", e);
    } finally {
      setLoading(false);
    }
  };

  const fetchCalendarBookings = async () => {
    try {
      const start = format(startOfMonth(currentMonth), 'yyyy-MM-dd');
      const end = format(endOfMonth(currentMonth), 'yyyy-MM-dd');
      const res = await axios.get(`${API}/admin/calendar?start=${start}&end=${end}`);
      setBookings(res.data);
    } catch (e) {
      console.error("Error fetching calendar bookings:", e);
    }
  };

  const handleDayClick = (day) => {
    setSelectedDay(day);
    const dateStr = format(day, 'yyyy-MM-dd');
    const filtered = bookings.filter(b => b.date === dateStr);
    setDayBookings(filtered.sort((a, b) => a.time.localeCompare(b.time)));
  };

  const updateBookingStatus = async (bookingId, status) => {
    try {
      await axios.patch(`${API}/admin/bookings/${bookingId}/status?status=${status}`);
      toast.success("Stato aggiornato");
      fetchCalendarBookings();
      fetchData();
      if (selectedDay) handleDayClick(selectedDay);
    } catch (e) {
      toast.error("Errore nell'aggiornamento");
    }
  };

  const addGalleryItem = async () => {
    if (!newGalleryItem.title || !newGalleryItem.media_url) {
      toast.error("Compila titolo e URL");
      return;
    }
    try {
      await axios.post(`${API}/gallery`, newGalleryItem);
      toast.success("Elemento aggiunto");
      setNewGalleryItem({ title: "", description: "", media_url: "", media_type: "image" });
      fetchData();
    } catch (e) {
      toast.error("Errore nell'aggiunta");
    }
  };

  const deleteGalleryItem = async (itemId) => {
    try {
      await axios.delete(`${API}/gallery/${itemId}`);
      toast.success("Elemento eliminato");
      fetchData();
    } catch (e) {
      toast.error("Errore nell'eliminazione");
    }
  };

  const getBookingsForDay = (day) => {
    const dateStr = format(day, 'yyyy-MM-dd');
    return bookings.filter(b => b.date === dateStr);
  };

  const daysInMonth = eachDayOfInterval({
    start: startOfMonth(currentMonth),
    end: endOfMonth(currentMonth)
  });

  const firstDayOfMonth = startOfMonth(currentMonth).getDay();
  const emptyDays = firstDayOfMonth === 0 ? 6 : firstDayOfMonth - 1;

  if (authLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Scissors className="w-10 h-10 animate-pulse" />
      </div>
    );
  }

  if (!admin) return null;

  return (
    <div className="min-h-screen pt-24 pb-12 bg-[#0a0a0a]" data-testid="admin-page">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="mb-8">
          <p className="text-xs uppercase tracking-[0.3em] text-[#C9A962] mb-4">Pannello Amministratore</p>
          <h1 className="font-serif text-4xl tracking-tight text-white">DASHBOARD</h1>
        </div>

        {/* Stats */}
        {stats && (
          <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-8">
            <div className="bg-neutral-900 border border-neutral-800 p-6" data-testid="stat-total-bookings">
              <BarChart3 className="w-6 h-6 mb-4 text-[#C9A962]" />
              <p className="text-3xl font-serif mb-1 text-white">{stats.total_bookings}</p>
              <p className="text-xs uppercase tracking-[0.1em] text-neutral-500">Prenotazioni Totali</p>
            </div>
            <div className="bg-neutral-900 border border-neutral-800 p-6" data-testid="stat-today-bookings">
              <CalendarIcon className="w-6 h-6 mb-4 text-[#C9A962]" />
              <p className="text-3xl font-serif mb-1 text-white">{stats.today_bookings}</p>
              <p className="text-xs uppercase tracking-[0.1em] text-neutral-500">Prenotazioni Oggi</p>
            </div>
            <div className="bg-neutral-900 border border-neutral-800 p-6" data-testid="stat-total-users">
              <Users className="w-6 h-6 mb-4 text-[#C9A962]" />
              <p className="text-3xl font-serif mb-1 text-white">{stats.total_users}</p>
              <p className="text-xs uppercase tracking-[0.1em] text-neutral-500">Clienti Registrati</p>
            </div>
          </div>
        )}

        <Tabs defaultValue="calendar" className="space-y-6">
          <TabsList className="bg-neutral-900 border border-neutral-800 p-1 flex-wrap h-auto">
            <TabsTrigger value="calendar" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black px-4 py-2 text-xs uppercase tracking-[0.1em] text-white" data-testid="tab-calendar">
              Calendario
            </TabsTrigger>
            <TabsTrigger value="services" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black px-4 py-2 text-xs uppercase tracking-[0.1em] text-white" data-testid="tab-services">
              Servizi
            </TabsTrigger>
            <TabsTrigger value="products" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black px-4 py-2 text-xs uppercase tracking-[0.1em] text-white" data-testid="tab-products">
              Prodotti
            </TabsTrigger>
            <TabsTrigger value="vacation" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black px-4 py-2 text-xs uppercase tracking-[0.1em] text-white" data-testid="tab-vacation">
              Ferie
            </TabsTrigger>
            {admin?.is_super_admin && (
              <TabsTrigger value="barbers" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black px-4 py-2 text-xs uppercase tracking-[0.1em] text-white" data-testid="tab-barbers">
                Barbieri
              </TabsTrigger>
            )}
            <TabsTrigger value="gallery" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black px-4 py-2 text-xs uppercase tracking-[0.1em] text-white" data-testid="tab-gallery">
              Gallery
            </TabsTrigger>
            <TabsTrigger value="qrcode" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black px-4 py-2 text-xs uppercase tracking-[0.1em] text-white" data-testid="tab-qrcode">
              QR Code
            </TabsTrigger>
            {admin?.is_super_admin && (
              <TabsTrigger value="settings" className="data-[state=active]:bg-[#C9A962] data-[state=active]:text-black px-4 py-2 text-xs uppercase tracking-[0.1em] text-white" data-testid="tab-settings">
                Impostazioni
              </TabsTrigger>
            )}
          </TabsList>

          {/* Calendar Tab */}
          <TabsContent value="calendar">
            <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
              {/* Calendar */}
              <div className="lg:col-span-2 bg-neutral-900 border border-neutral-800 p-6">
                {/* Month Navigation */}
                <div className="flex items-center justify-between mb-6">
                  <button 
                    onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() - 1))}
                    className="p-2 hover:bg-neutral-800 rounded transition-colors"
                  >
                    <ChevronLeft className="w-5 h-5 text-[#C9A962]" />
                  </button>
                  <h2 className="font-serif text-2xl text-white">
                    {format(currentMonth, 'MMMM yyyy', { locale: it })}
                  </h2>
                  <button 
                    onClick={() => setCurrentMonth(prev => new Date(prev.getFullYear(), prev.getMonth() + 1))}
                    className="p-2 hover:bg-neutral-800 rounded transition-colors"
                  >
                    <ChevronRight className="w-5 h-5 text-[#C9A962]" />
                  </button>
                </div>

                {/* Weekday Headers */}
                <div className="grid grid-cols-7 gap-1 mb-2">
                  {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map(day => (
                    <div key={day} className="text-center text-xs uppercase tracking-wider text-neutral-500 py-2">
                      {day}
                    </div>
                  ))}
                </div>

                {/* Calendar Days */}
                <div className="grid grid-cols-7 gap-1">
                  {/* Empty cells for days before start of month */}
                  {Array.from({ length: emptyDays }).map((_, i) => (
                    <div key={`empty-${i}`} className="aspect-square" />
                  ))}
                  
                  {daysInMonth.map(day => {
                    const dayBookingsCount = getBookingsForDay(day).length;
                    const isToday = isSameDay(day, new Date());
                    const isSelected = selectedDay && isSameDay(day, selectedDay);
                    const isClosed = isMonday(day) || isSunday(day);
                    
                    return (
                      <button
                        key={day.toString()}
                        onClick={() => !isClosed && handleDayClick(day)}
                        disabled={isClosed}
                        className={`aspect-square p-1 flex flex-col items-center justify-center relative transition-all ${
                          isClosed 
                            ? 'bg-neutral-800/30 text-neutral-700 cursor-not-allowed' 
                            : isSelected 
                              ? 'bg-[#C9A962] text-black' 
                              : isToday 
                                ? 'bg-neutral-800 text-white ring-2 ring-[#C9A962]' 
                                : 'bg-neutral-800/50 text-white hover:bg-neutral-700'
                        }`}
                        data-testid={`calendar-day-${format(day, 'yyyy-MM-dd')}`}
                      >
                        <span className="text-sm font-medium">{format(day, 'd')}</span>
                        {dayBookingsCount > 0 && !isClosed && (
                          <span className={`text-[10px] mt-0.5 px-1.5 rounded-full ${
                            isSelected ? 'bg-black/20 text-black' : 'bg-[#C9A962] text-black'
                          }`}>
                            {dayBookingsCount}
                          </span>
                        )}
                      </button>
                    );
                  })}
                </div>

                <div className="mt-4 flex items-center gap-4 text-xs text-neutral-500">
                  <span className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-[#C9A962] rounded-full" /> Appuntamenti
                  </span>
                  <span className="flex items-center gap-2">
                    <div className="w-3 h-3 bg-neutral-800/30 rounded" /> Chiuso
                  </span>
                </div>
              </div>

              {/* Day Detail */}
              <div className="bg-neutral-900 border border-neutral-800 p-6">
                <h3 className="font-serif text-xl text-white mb-4">
                  {selectedDay 
                    ? format(selectedDay, "EEEE d MMMM", { locale: it }) 
                    : "Seleziona un giorno"}
                </h3>
                
                {selectedDay && (
                  <div className="space-y-3 max-h-[500px] overflow-y-auto">
                    {dayBookings.length === 0 ? (
                      <p className="text-neutral-500 text-sm">Nessun appuntamento</p>
                    ) : (
                      dayBookings.map(booking => (
                        <div 
                          key={booking.id} 
                          className="bg-neutral-800 p-4 border-l-4 border-[#C9A962]"
                          data-testid={`booking-detail-${booking.id}`}
                        >
                          <div className="flex items-start justify-between mb-2">
                            <span className="text-[#C9A962] font-mono text-lg">{booking.time}</span>
                            <span className={`text-[10px] uppercase px-2 py-1 ${
                              booking.status === 'confirmed' ? 'bg-[#C9A962] text-black' :
                              booking.status === 'completed' ? 'bg-green-500/20 text-green-400' :
                              'bg-red-500/20 text-red-400'
                            }`}>
                              {booking.status}
                            </span>
                          </div>
                          <p className="text-white font-medium">{booking.user_name}</p>
                          <p className="text-neutral-400 text-sm">{booking.service_name}</p>
                          <div className="mt-2 space-y-1">
                            <p className="text-neutral-500 text-xs flex items-center gap-2" data-testid={`booking-email-${booking.id}`}>
                              <Mail className="w-3 h-3" /> {booking.user_email}
                            </p>
                            <p className="text-neutral-500 text-xs flex items-center gap-2" data-testid={`booking-phone-${booking.id}`}>
                              <Phone className="w-3 h-3" /> {booking.user_phone || "—"}
                            </p>
                          </div>
                          {booking.status === 'confirmed' && (
                            <div className="flex gap-2 mt-3">
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'completed')}
                                className="flex-1 py-2 bg-green-500/20 text-green-400 text-xs uppercase tracking-wider hover:bg-green-500/30 transition-colors"
                              >
                                Completa
                              </button>
                              <button
                                onClick={() => updateBookingStatus(booking.id, 'cancelled')}
                                className="flex-1 py-2 bg-red-500/20 text-red-400 text-xs uppercase tracking-wider hover:bg-red-500/30 transition-colors"
                              >
                                Cancella
                              </button>
                            </div>
                          )}
                        </div>
                      ))
                    )}
                  </div>
                )}
              </div>
            </div>
          </TabsContent>

          {/* Barbers Tab */}
          <TabsContent value="barbers">
            <BarbersManager />
          </TabsContent>

          {/* Services Tab */}
          <TabsContent value="services">
            <ServicesManager adminInfo={admin} />
          </TabsContent>

          {/* Products Tab */}
          <TabsContent value="products">
            <ProductsManager />
          </TabsContent>

          {/* Vacation Tab */}
          <TabsContent value="vacation">
            <VacationManager />
          </TabsContent>

          {/* Settings Tab */}
          <TabsContent value="settings">
            <SettingsManager />
          </TabsContent>

          {/* Gallery Tab */}
          <TabsContent value="gallery">
            <div className="bg-white border border-neutral-200 p-6">
              {/* Add New Item */}
              <div className="mb-8 pb-8 border-b border-neutral-200">
                <h3 className="text-xs uppercase tracking-[0.2em] mb-6">Aggiungi Nuovo Elemento</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                  <input
                    type="text"
                    placeholder="Titolo"
                    value={newGalleryItem.title}
                    onChange={(e) => setNewGalleryItem({ ...newGalleryItem, title: e.target.value })}
                    className="border border-neutral-200 px-4 py-3"
                    data-testid="gallery-title-input"
                  />
                  <input
                    type="text"
                    placeholder="URL immagine/video"
                    value={newGalleryItem.media_url}
                    onChange={(e) => setNewGalleryItem({ ...newGalleryItem, media_url: e.target.value })}
                    className="border border-neutral-200 px-4 py-3"
                    data-testid="gallery-url-input"
                  />
                  <select
                    value={newGalleryItem.media_type}
                    onChange={(e) => setNewGalleryItem({ ...newGalleryItem, media_type: e.target.value })}
                    className="border border-neutral-200 px-4 py-3"
                    data-testid="gallery-type-select"
                  >
                    <option value="image">Immagine</option>
                    <option value="video">Video</option>
                  </select>
                  <button
                    onClick={addGalleryItem}
                    className="bg-black text-white px-6 py-3 uppercase tracking-[0.1em] text-sm font-bold hover:bg-neutral-800 transition-colors flex items-center justify-center gap-2"
                    data-testid="add-gallery-btn"
                  >
                    <Plus className="w-4 h-4" /> Aggiungi
                  </button>
                </div>
                <input
                  type="text"
                  placeholder="Descrizione (opzionale)"
                  value={newGalleryItem.description}
                  onChange={(e) => setNewGalleryItem({ ...newGalleryItem, description: e.target.value })}
                  className="border border-neutral-200 px-4 py-3 w-full mt-4"
                  data-testid="gallery-description-input"
                />
              </div>

              {/* Gallery Grid */}
              <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                {gallery.map((item) => (
                  <div key={item.id} className="relative group" data-testid={`admin-gallery-${item.id}`}>
                    <div className="aspect-square overflow-hidden">
                      {item.media_type === "video" ? (
                        <video src={item.media_url} className="w-full h-full object-cover" />
                      ) : (
                        <img src={item.media_url} alt={item.title} className="w-full h-full object-cover" />
                      )}
                    </div>
                    <div className="absolute inset-0 bg-black/60 opacity-0 group-hover:opacity-100 transition-opacity flex items-center justify-center">
                      <button
                        onClick={() => deleteGalleryItem(item.id)}
                        className="bg-white text-red-600 p-2 hover:bg-red-50"
                        data-testid={`delete-gallery-${item.id}`}
                      >
                        <Trash2 className="w-5 h-5" />
                      </button>
                    </div>
                    <p className="mt-2 text-sm truncate">{item.title}</p>
                  </div>
                ))}
              </div>
              {gallery.length === 0 && (
                <p className="text-center py-8 text-neutral-500">Nessun elemento nella gallery</p>
              )}
            </div>
          </TabsContent>

          <TabsContent value="qrcode">
            <QRCodeManager />
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
};

// App Router with Auth Callback detection
const AppRouter = () => {
  const location = useLocation();
  
  // Check URL fragment (not query params) for session_id - MUST be synchronous
  if (location.hash?.includes('session_id=')) {
    return <AuthCallback />;
  }
  
  return (
    <Routes>
      <Route path="/" element={<HomePage />} />
      <Route path="/login" element={<LoginPage />} />
      <Route path="/admin/login" element={<AdminLoginPage />} />
      <Route path="/gallery" element={<GalleryPage />} />
      <Route path="/prenota" element={<BookingPage />} />
      <Route path="/dashboard" element={<DashboardPage />} />
      <Route path="/admin" element={<AdminPage />} />
      <Route path="*" element={<NotFoundPage />} />
    </Routes>
  );
};

// Main App
function App() {
  return (
    <AuthProvider>
      <AdminAuthProvider>
        <SettingsProvider>
          <div className="App">
            <div className="noise-overlay" />
            <BrowserRouter>
              <Navigation />
              <AppRouter />
            </BrowserRouter>
            <Toaster position="top-right" />
          </div>
        </SettingsProvider>
      </AdminAuthProvider>
    </AuthProvider>
  );
}

export default App;
